const HA_URL   = localStorage.getItem("livato_ha_url")  || "http://localhost:8123";
const HA_TOKEN = localStorage.getItem("livato_ha_token") || "";
const LIVATO_SERVER = "https://livato-server-production.up.railway.app";

import { useState, useEffect, useRef, memo } from "react";

// ═══════════════════════════════════════════════════════════════════════
// LIVATO — AI Smart Home Dashboard
// ═══════════════════════════════════════════════════════════════════════

const ROOMS = [
  { id:"entrance", label:"Entrance",    x:0,   y:0,   w:2,   h:1,   color:"#6366f1" },
  { id:"living",   label:"Living Room", x:0,   y:1,   w:2,   h:2,   color:"#f59e0b" },
  { id:"kitchen",  label:"Kitchen",     x:2,   y:0,   w:2,   h:1.5, color:"#10b981" },
  { id:"bedroom",  label:"Bedroom",     x:2,   y:1.5, w:1.5, h:1.5, color:"#ec4899" },
  { id:"bathroom", label:"Bathroom",    x:3.5, y:1.5, w:0.5, h:1.5, color:"#06b6d4" },
  { id:"garage",   label:"Garage",      x:0,   y:3,   w:2,   h:1,   color:"#78716c" },
  { id:"garden",   label:"Garden",      x:2,   y:3,   w:2,   h:1,   color:"#5db832" },
];

const DEVICES_INIT = [
  { id:1,  name:"Front Door Camera", room:"entrance", type:"camera",     icon:"📷", online:true,  x:0.3, y:0.3, state:"watching", capabilities:{ triggers:["motion_detected","person_detected","package_detected"], actions:[] }},
  { id:2,  name:"Porch Light",       room:"entrance", type:"light",      icon:"💡", online:true,  x:1.5, y:0.6, state:"off",      brightness:80,  capabilities:{ triggers:["turned_on","turned_off"], actions:["turn_on","turn_off","set_brightness","set_color"] }},
  { id:3,  name:"Smart Lock",        room:"entrance", type:"lock",       icon:"🔒", online:true,  x:0.8, y:0.7, state:"locked",   capabilities:{ triggers:["door_opened","door_closed"], actions:["lock","unlock"] }},
  { id:4,  name:"Sofa Lamp",         room:"living",   type:"light",      icon:"🪔", online:true,  x:0.3, y:1.4, state:"on",       brightness:60,  capabilities:{ triggers:["turned_on","turned_off"], actions:["turn_on","turn_off","set_brightness","set_color"] }},
  { id:5,  name:"Motion Sensor",     room:"living",   type:"sensor",     icon:"👁️", online:true,  x:1.0, y:2.3, state:"clear",    capabilities:{ triggers:["motion_detected","no_motion"], actions:[] }},
  { id:6,  name:"Thermostat",        room:"living",   type:"thermostat", icon:"🌡️", online:true,  x:1.7, y:1.5, state:"21°C",     capabilities:{ triggers:["temp_above","temp_below"], actions:["set_temp","heat_on","cool_on"] }},
  { id:7,  name:"TV",                room:"living",   type:"tv",         icon:"📺", online:true,  x:0.8, y:2.7, state:"off",      capabilities:{ triggers:["turned_on","turned_off"], actions:["turn_on","turn_off"] }},
  { id:8,  name:"Kitchen Light",     room:"kitchen",  type:"light",      icon:"💡", online:true,  x:3.0, y:0.6, state:"on",       brightness:100, capabilities:{ triggers:["turned_on","turned_off"], actions:["turn_on","turn_off","set_brightness"] }},
  { id:9,  name:"Smoke Detector",    room:"kitchen",  type:"sensor",     icon:"🚨", online:true,  x:2.5, y:0.3, state:"clear",    capabilities:{ triggers:["smoke_detected","co_detected"], actions:[] }},
  { id:10, name:"Bedroom Light",     room:"bedroom",  type:"light",      icon:"💡", online:false, x:2.5, y:2.2, state:"off",      brightness:40,  capabilities:{ triggers:["turned_on","turned_off"], actions:["turn_on","turn_off","set_brightness","set_color"] }},
  { id:11, name:"Sleep Sensor",      room:"bedroom",  type:"sensor",     icon:"😴", online:true,  x:3.1, y:2.7, state:"awake",    capabilities:{ triggers:["sleeping","awake"], actions:[] }},
  { id:12, name:"Garage Door",       room:"garage",   type:"garage",     icon:"🚗", online:true,  x:0.8, y:3.5, state:"closed",   capabilities:{ triggers:["opened","closed"], actions:["open","close"] }},
  { id:13, name:"Sprinklers",        room:"garden",   type:"sprinkler",  icon:"💧", online:true,  x:3.2, y:3.4, state:"off",      capabilities:{ triggers:["turned_on","turned_off"], actions:["turn_on","turn_off","set_zone","set_duration"] }},
  { id:14, name:"Weather Station",   room:"garden",   type:"sensor",     icon:"🌤️", online:true,  x:2.4, y:3.6, state:"22°C",     capabilities:{ triggers:["rain_detected","no_rain","uv_high","uv_normal","wind_high","wind_calm","temp_above","temp_below","humidity_high","humidity_low"], actions:[] }},
];

const TRIGGER_LABELS = {
  motion_detected:"detects motion", person_detected:"detects a person",
  package_detected:"detects a package", door_opened:"door opens", door_closed:"door closes",
  turned_on:"turns on", turned_off:"turns off",
  temp_above:"temperature rises above", temp_below:"temperature drops below",
  smoke_detected:"detects smoke", co_detected:"detects CO",
  sleeping:"sleep starts", awake:"wakes up", opened:"opens", closed:"closes",
  no_motion:"detects no motion", rain_detected:"🌧 Rain detected", no_rain:"☀️ No rain forecast",
  uv_high:"☀️ UV index is high", wind_high:"💨 Wind is high",
  humidity_high:"💦 Humidity is high", humidity_low:"🏜 Humidity is low",
};

const ACTION_LABELS = {
  turn_on:"Turn On", turn_off:"Turn Off", set_brightness:"Set Brightness", set_color:"Change Color",
  lock:"Lock", unlock:"Unlock", set_temp:"Set Temperature", heat_on:"Start Heating",
  cool_on:"Start Cooling", open:"Open", close:"Close", set_zone:"Set Zone", set_duration:"Set Duration",
};

const DAYS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];


// ⚙️ PLAYLIST REMOVED — connect Spotify / Apple Music / YouTube for real playback

const INIT_AUTOMATIONS = [
  { id:1, name:"Welcome Home",       enabled:true, lastTriggered:"2 min ago",  trigger:{deviceId:1, event:"person_detected"},  condition:{useTime:true, timeFrom:"17:00", timeTo:"23:00", days:[0,1,2,3,4]},       actions:[{type:"device",deviceId:2,action:"turn_on"},{type:"notify",message:"Someone arrived home 🏠"}] },
  { id:2, name:"Night Lock",         enabled:true, lastTriggered:"Yesterday",  trigger:{deviceId:3, event:"door_closed"},       condition:{useTime:true, timeFrom:"21:00", timeTo:"06:00", days:[0,1,2,3,4,5,6]},   actions:[{type:"device",deviceId:3,action:"lock"}] },
  { id:3, name:"Skip Watering Rain", enabled:true, lastTriggered:"3 days ago", trigger:{deviceId:14,event:"rain_detected"},     condition:{useTime:true, timeFrom:"06:00", timeTo:"09:00", days:[0,1,2,3,4,5,6]},   actions:[{type:"device",deviceId:13,action:"turn_off"},{type:"notify",message:"🌧 Skipping sprinklers — rain detected!"}] },
];

const QUICK_SCENES = [
  { id:"morning", label:"Morning", icon:"🌅", color:"#f59e0b", actions:["lights_on","thermostat_22"] },
  { id:"movie",   label:"Movie",   icon:"🎬", color:"#6366f1", actions:["lights_dim","tv_on"] },
  { id:"sleep",   label:"Sleep",   icon:"🌙", color:"#06b6d4", actions:["lights_off","lock_all"] },
  { id:"away",    label:"Away",    icon:"🚪", color:"#ec4899", actions:["lights_off","lock_all","arm"] },
];

const SMART_SUGGESTIONS = [
  { id:"s1", icon:"🌧", title:"Skip watering on rainy days",   desc:"Skips morning schedule when rain detected.",        savings:"Saves ~12,000 L/year",    trigger:{deviceId:14,event:"rain_detected"},  condition:{useTime:true, timeFrom:"06:00",timeTo:"09:00",days:[0,1,2,3,4,5,6]}, actions:[{type:"device",deviceId:13,action:"turn_off"},{type:"notify",message:"🌧 Skipping sprinklers — rain detected!"}] },
  { id:"s2", icon:"🌡️", title:"Water when it's cooler",        desc:"Triggers only when temp is below 28°C.",           savings:"Saves ~30% water loss",    trigger:{deviceId:14,event:"temp_below"},     condition:{useTime:true, timeFrom:"06:00",timeTo:"09:00",days:[0,2,4,6]},     actions:[{type:"device",deviceId:13,action:"turn_on"}] },
  { id:"s3", icon:"💨", title:"Pause sprinklers in high wind", desc:"Stops watering when wind exceeds 35 km/h.",        savings:"Prevents run-off waste",   trigger:{deviceId:14,event:"wind_high"},      condition:{useTime:false,days:[0,1,2,3,4,5,6]},                               actions:[{type:"device",deviceId:13,action:"turn_off"},{type:"notify",message:"💨 Pausing sprinklers — too windy!"}] },
  { id:"s4", icon:"🏜", title:"Water when humidity is low",    desc:"Waters automatically when humidity is low.",       savings:"Waters only when needed",  trigger:{deviceId:14,event:"humidity_low"},   condition:{useTime:true, timeFrom:"07:00",timeTo:"10:00",days:[0,2,4,6]},     actions:[{type:"device",deviceId:13,action:"turn_on"}] },
];

const WEATHER_TRIGGERS = [
  { id:"rain_detected", label:"Rain Detected",     icon:"🌧", unit:"%",    min:0,  max:100, default:70 },
  { id:"temp_above",    label:"Temperature Above", icon:"🌡️", unit:"°C",   min:15, max:45,  default:30 },
  { id:"temp_below",    label:"Temperature Below", icon:"❄️", unit:"°C",   min:0,  max:20,  default:5  },
  { id:"uv_high",       label:"UV Index High",     icon:"☀️", unit:"",     min:3,  max:11,  default:7  },
  { id:"humidity_high", label:"Humidity High",     icon:"💦", unit:"%",    min:50, max:100, default:80 },
  { id:"humidity_low",  label:"Humidity Low",      icon:"🏜", unit:"%",    min:10, max:50,  default:25 },
  { id:"wind_high",     label:"Wind Speed High",   icon:"💨", unit:"km/h", min:15, max:80,  default:35 },
];

// ─── Helpers ────────────────────────────────────────────────────────────────
const deviceById = id => DEVICES_INIT.find(d => d.id === id);
const roomById   = id => ROOMS.find(r => r.id === id);
const fmtTime    = s  => `${Math.floor(s/60)}:${String(s%60).padStart(2,"0")}`;

// ─── Shared UI ──────────────────────────────────────────────────────────────
function Pill({ children, color="#5db832" }) {
  return <span style={{ display:"inline-flex",alignItems:"center",gap:4,padding:"3px 9px",borderRadius:8,background:color+"22",border:`1px solid ${color}44`,color,fontSize:11,fontWeight:600,whiteSpace:"nowrap" }}>{children}</span>;
}
function Toggle({ on, onChange, color="#5db832" }) {
  return (
    <button onClick={() => onChange(!on)} style={{ width:44,height:24,borderRadius:12,background:on?color:"rgba(255,255,255,0.1)",border:"none",cursor:"pointer",position:"relative",transition:"background .25s",flexShrink:0 }}>
      <div style={{ width:18,height:18,borderRadius:"50%",background:"#fff",position:"absolute",top:3,left:on?23:3,transition:"left .25s",boxShadow:"0 1px 4px rgba(0,0,0,.3)" }} />
    </button>
  );
}
function BtnPrimary({ children, onClick, disabled, style={} }) {
  return <button onClick={onClick} disabled={disabled} style={{ flex:2,padding:"13px",borderRadius:14,border:"none",background:disabled?"rgba(255,255,255,.06)":"linear-gradient(135deg,#5db832,#3d7a1a)",color:disabled?"#334155":"#fff",fontWeight:700,fontSize:14,cursor:disabled?"not-allowed":"pointer",boxShadow:disabled?"none":"0 4px 16px rgba(93,184,50,.3)",transition:"all .2s",...style }}>{children}</button>;
}
function BackBtn({ onClick }) {
  return <button onClick={onClick} style={{ flex:1,padding:"13px",borderRadius:14,border:"1.5px solid rgba(255,255,255,.09)",background:"transparent",color:"#475569",fontWeight:600,fontSize:14,cursor:"pointer" }}>← Back</button>;
}

// ─── Music Visualizer ───────────────────────────────────────────────────────
function Visualizer({ playing, color="#5db832" }) {
  return (
    <div style={{ display:"flex",alignItems:"flex-end",gap:2,height:32 }}>
      {Array.from({length:28}).map((_,i) => (
        <div key={i} style={{ width:3,borderRadius:2,background:`${color}${playing?"cc":"44"}`,height:playing?undefined:4,animation:playing?`bar${i%5} ${0.6+(i%7)*0.12}s ease-in-out infinite alternate`:"none" }} />
      ))}
    </div>
  );
}

// ─── Music Player ───────────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════
// LIVATO MUSIC COMPONENT — Replace existing MusicPlayer function
// Paste this entire block replacing:
//   function MusicPlayer({ compact, onGoToMusic }) { ... } (lines 181-287 approx)
// ═══════════════════════════════════════════════════════════════════════

const MUSIC_SERVICES = [
  { id:"spotify",  name:"Spotify",       icon:"🎵", color:"#1db954", bg:"#0d3320", desc:"600M+ users · Free & Premium" },
  { id:"youtube",  name:"YouTube Music", icon:"▶",  color:"#ff0000", bg:"#330000", desc:"100M+ songs · Free with ads" },
  { id:"apple",    name:"Apple Music",   icon:"🎸", color:"#fc3c44", bg:"#330010", desc:"100M+ songs · Subscription" },
  { id:"amazon",   name:"Amazon Music",  icon:"🎶", color:"#00a8e1", bg:"#00233a", desc:"90M+ songs · Prime included" },
];

// ⚙️ MOOD_COLOR removed with demo playlist

function MusicServiceConnect({ onConnect, connectedServices }) {
  return (
    <div style={{ padding:"8px 0" }}>
      <div style={{ fontSize:13, fontWeight:700, color:"#f1f5f9", marginBottom:6 }}>Connect your music</div>
      <div style={{ fontSize:12, color:"#475569", marginBottom:16, lineHeight:1.5 }}>
        Sign in once — your playlists appear automatically. AI can control your music by request.
      </div>
      <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
        {MUSIC_SERVICES.map(svc => {
          const connected = connectedServices.includes(svc.id);
          return (
            <button key={svc.id} onClick={() => onConnect(svc)}
              style={{
                display:"flex", alignItems:"center", gap:14,
                padding:"14px 16px", borderRadius:16, cursor:"pointer",
                border:`1.5px solid ${connected ? svc.color + "55" : "rgba(255,255,255,.08)"}`,
                background: connected ? svc.bg : "rgba(255,255,255,.03)",
                textAlign:"left", transition:"all .2s"
              }}>
              <div style={{
                width:42, height:42, borderRadius:12, flexShrink:0,
                background: connected ? svc.color + "22" : "rgba(255,255,255,.06)",
                border:`1.5px solid ${connected ? svc.color + "44" : "rgba(255,255,255,.1)"}`,
                display:"flex", alignItems:"center", justifyContent:"center",
                fontSize:20, color: svc.color
              }}>{svc.icon}</div>
              <div style={{ flex:1 }}>
                <div style={{ color: connected ? "#f1f5f9" : "#94a3b8", fontWeight:700, fontSize:14 }}>{svc.name}</div>
                <div style={{ color:"#475569", fontSize:11, marginTop:2 }}>{svc.desc}</div>
              </div>
              <div style={{
                fontSize:11, fontWeight:700, padding:"5px 12px", borderRadius:8,
                background: connected ? svc.color + "22" : "rgba(255,255,255,.06)",
                color: connected ? svc.color : "#475569",
                border:`1px solid ${connected ? svc.color + "44" : "rgba(255,255,255,.08)"}`
              }}>
                {connected ? "✓ Connected" : "Connect"}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Global now-playing state shared via localStorage + custom event
const setNowPlaying = (track) => {
  if (track) {
    localStorage.setItem("livato_now_playing", JSON.stringify(track));
  } else {
    localStorage.removeItem("livato_now_playing");
  }
  window.dispatchEvent(new Event("livato_music_change"));
};

const getNowPlaying = () => {
  try { const s = localStorage.getItem("livato_now_playing"); return s ? JSON.parse(s) : null; } catch { return null; }
};

// Send pause/play commands to the persistent iframe via postMessage
const ytCommand = (func) => {
  const iframe = document.getElementById("yt-persistent-player");
  iframe?.contentWindow?.postMessage(JSON.stringify({ event:"command", func, args:[] }), "*");
};

const PersistentPlayer = memo(
  function PersistentPlayer({ trackId }) {
    return (
      <div style={{ position:"fixed", bottom:90, left:0, right:0, zIndex:90,
        display:"flex", justifyContent:"center", pointerEvents:"none" }}>
        <div style={{ width:1, height:1, overflow:"hidden", pointerEvents:"none" }}>
          <iframe
            id="yt-persistent-player"
            src={`https://www.youtube.com/embed/${trackId}?autoplay=1&rel=0&modestbranding=1&enablejsapi=1`}
            frameBorder="0"
            allow="autoplay; encrypted-media"
            title="YouTube Music"
            style={{ width:1, height:1 }}
          />
        </div>
      </div>
    );
  },
  (prev, next) => prev.trackId === next.trackId
);

// ─── Shared Now Playing Card ─────────────────────────────────────────────────
function NowPlayingCard({ track, paused, onPausePlay, onStop, compact = false }) {
  if (!track) {
    return (
      <div onClick={() => window.dispatchEvent(new CustomEvent("livato_goto_tab", { detail:"music" }))}
        style={{ background:"rgba(255,255,255,.03)", border:"1.5px dashed rgba(255,255,255,.12)", borderRadius:20,
          padding:compact ? "14px 16px" : "20px 16px", display:"flex", alignItems:"center", gap:14, cursor:"pointer" }}>
        <div style={{ width:compact?42:48, height:compact?42:48, borderRadius:12, background:"rgba(93,184,50,.08)",
          border:"1.5px solid rgba(93,184,50,.2)", display:"flex", alignItems:"center", justifyContent:"center",
          fontSize:compact?20:24, flexShrink:0 }}>🎵</div>
        <div style={{ flex:1 }}>
          <div style={{ color:"#94a3b8", fontWeight:700, fontSize:compact?13:14 }}>No music playing</div>
          <div style={{ color:"#334155", fontSize:11, marginTop:2 }}>Tap to search and play music</div>
        </div>
        <div style={{ color:"#5db832", fontSize:20 }}>→</div>
      </div>
    );
  }
  return (
    <div style={{ background:"rgba(255,0,0,.06)", border:"1.5px solid rgba(255,0,0,.2)", borderRadius:20,
      padding:compact ? "12px 14px" : "16px", display:"flex", alignItems:"center", gap:12 }}>
      {track.thumb
        ? <img src={track.thumb} alt="" style={{ width:compact?42:52, height:compact?42:52, borderRadius:10, objectFit:"cover", flexShrink:0 }} />
        : <div style={{ width:compact?42:52, height:compact?42:52, borderRadius:12, background:"rgba(255,0,0,.12)",
            display:"flex", alignItems:"center", justifyContent:"center", fontSize:compact?18:22, flexShrink:0, color:"#ff0000" }}>▶</div>
      }
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ color:"#f1f5f9", fontWeight:700, fontSize:compact?13:14, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{track.title}</div>
        <div style={{ color:"#64748b", fontSize:11, marginTop:2 }}>{track.artist}</div>
        <div style={{ display:"flex", alignItems:"center", gap:6, marginTop:4 }}>
          {!paused && (
            <div style={{ display:"flex", alignItems:"flex-end", gap:2, height:14 }}>
              {[6,10,8,14,9].map((h,i) => (
                <div key={i} style={{ width:2, borderRadius:2, background:"#ff000099", height:h,
                  animation:`bar${i%5} ${0.6+i*0.1}s ease-in-out infinite alternate` }} />
              ))}
            </div>
          )}
          <span style={{ color:"#ff0000", fontSize:10, fontWeight:700 }}>▶ YouTube Music</span>
        </div>
      </div>
      <button onClick={onPausePlay}
        style={{ width:36, height:36, borderRadius:"50%", background:"rgba(255,0,0,.12)",
          border:"1.5px solid rgba(255,0,0,.25)", cursor:"pointer", color:"#ff4444",
          fontSize:14, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
        {paused ? "▶" : "⏸"}
      </button>
      <button onClick={onStop}
        style={{ width:36, height:36, borderRadius:"50%", background:"rgba(255,0,0,.12)",
          border:"1.5px solid rgba(255,0,0,.25)", cursor:"pointer", color:"#ff4444",
          fontSize:14, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
        ■
      </button>
    </div>
  );
}

// ─── YouTube Music Search ────────────────────────────────────────────────────
// Search only — playback handled by PersistentPlayer at App level
function YouTubeMusicSearch() {
  const [query, setQuery]     = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentId, setCurrentId] = useState(() => getNowPlaying()?.id || null);

  useEffect(() => {
    const handler = () => setCurrentId(getNowPlaying()?.id || null);
    window.addEventListener("livato_music_change", handler);
    return () => window.removeEventListener("livato_music_change", handler);
  }, []);

  const YT_KEY = process.env.REACT_APP_YOUTUBE_API_KEY || "";

  const search = async () => {
    if (!query.trim() || !YT_KEY) return;
    setLoading(true);
    try {
      const res = await fetch(
        `https://www.googleapis.com/youtube/v3/search?part=snippet&q=${encodeURIComponent(query + " official audio")}&type=video&videoCategoryId=10&maxResults=10&key=${YT_KEY}`
      );
      const data = await res.json();
      if (data.items) {
        setResults(data.items.map(item => ({
          id: item.id.videoId,
          title: item.snippet.title.replace(/\s*\(.*?\)\s*/g, "").replace(/\s*\[.*?\]\s*/g, ""),
          artist: item.snippet.channelTitle.replace(" - Topic", ""),
          thumb: item.snippet.thumbnails.medium?.url,
        })));
      }
    } catch (err) { console.log("YouTube search error:", err); }
    setLoading(false);
  };

  if (!YT_KEY) {
    return (
      <div style={{ background:"rgba(255,165,0,.06)", border:"1.5px solid rgba(255,165,0,.2)", borderRadius:16, padding:"14px 16px" }}>
        <div style={{ color:"#f59e0b", fontWeight:700, fontSize:13, marginBottom:4 }}>⚙️ YouTube API key needed</div>
        <div style={{ color:"#64748b", fontSize:12, lineHeight:1.6 }}>
          Add <code style={{ color:"#a8e070" }}>REACT_APP_YOUTUBE_API_KEY</code> to your Railway frontend environment variables to enable music search.
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display:"flex", gap:8, marginBottom:14 }}>
        <input value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => e.key==="Enter" && search()}
          placeholder="Search songs, artists…"
          style={{ flex:1, padding:"11px 14px", borderRadius:12, border:"1.5px solid rgba(255,255,255,.1)",
            background:"rgba(255,255,255,.06)", color:"#f1f5f9", fontSize:13, outline:"none", fontFamily:"inherit" }} />
        <button onClick={search} disabled={loading}
          style={{ padding:"11px 16px", borderRadius:12, border:"none", background:"#ff0000",
            color:"#fff", fontWeight:700, fontSize:13, cursor:"pointer", flexShrink:0 }}>
          {loading ? "…" : "Search"}
        </button>
      </div>

      {!results.length && !loading && (
        <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:12 }}>
          {["Relaxing","Focus","Workout","Happy","Sleep","Party","Jazz","Classical"].map(mood => (
            <button key={mood} onClick={() => { setQuery(mood); setTimeout(search, 50); }}
              style={{ padding:"6px 14px", borderRadius:20, border:"1.5px solid rgba(255,255,255,.08)",
                background:"rgba(255,255,255,.04)", color:"#64748b", fontSize:12, fontWeight:600, cursor:"pointer" }}>
              {mood}
            </button>
          ))}
        </div>
      )}

      {loading && <div style={{ textAlign:"center", padding:"20px 0", color:"#475569", fontSize:13 }}>Searching…</div>}

      {results.length > 0 && (
        <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
          {results.map(track => {
            const isActive = currentId === track.id;
            return (
              <button key={track.id} onClick={() => setNowPlaying(track)}
                style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 12px", borderRadius:13,
                  border:`1.5px solid ${isActive ? "#ff000033" : "rgba(255,255,255,.06)"}`,
                  background:isActive ? "rgba(255,0,0,.08)" : "rgba(255,255,255,.03)", cursor:"pointer", textAlign:"left" }}>
                {track.thumb
                  ? <img src={track.thumb} alt="" style={{ width:44, height:44, borderRadius:8, objectFit:"cover", flexShrink:0 }} />
                  : <div style={{ width:44, height:44, borderRadius:8, background:"rgba(255,0,0,.1)",
                      display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, flexShrink:0, color:"#ff0000" }}>▶</div>
                }
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ color:isActive?"#f1f5f9":"#94a3b8", fontWeight:isActive?700:500, fontSize:12,
                    overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{track.title}</div>
                  <div style={{ color:"#334155", fontSize:11, marginTop:1 }}>{track.artist}</div>
                </div>
                {isActive && <span style={{ color:"#ff0000", fontSize:16, flexShrink:0 }}>▶</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Music Player ─────────────────────────────────────────────────────────────
// Single source of truth — compact on home, full on music tab, one PersistentPlayer
function MusicPlayer({ compact, onGoToMusic }) {
  const [ytNowPlaying, setYtNowPlaying] = useState(getNowPlaying);
  const [paused, setPaused] = useState(false);
  const [connectedServices, setConnectedServices] = useState(
    JSON.parse(localStorage.getItem("livato_music_services") || "[]")
  );

  useEffect(() => {
    const handler = () => {
      const np = getNowPlaying();
      setYtNowPlaying(np);
      if (np) setPaused(false); // new track always starts playing
    };
    window.addEventListener("livato_music_change", handler);
    return () => window.removeEventListener("livato_music_change", handler);
  }, []);

  const handlePausePlay = () => {
    ytCommand(paused ? "playVideo" : "pauseVideo");
    setPaused(p => !p);
  };

  const handleStop = () => { setNowPlaying(null); setPaused(false); };

  const handleConnect = (svc) => {
    // ⚙️ TODO: implement OAuth for each service (Spotify, Apple Music, Amazon)
    alert(`${svc.name} sign-in coming soon! We'll notify you when it's ready.`);
  };

  // ── Compact view (home screen) ──
  if (compact) {
    return (
      <NowPlayingCard
        track={ytNowPlaying}
        paused={paused}
        onPausePlay={handlePausePlay}
        onStop={handleStop}
        compact
      />
    );
  }

  // ── Full music tab ──
  return (
    <div>
      {/* Now playing — same card as home screen */}
      <div style={{ marginBottom:16 }}>
        <div style={{ fontSize:11, fontWeight:700, color:"#334155", letterSpacing:".07em", textTransform:"uppercase", marginBottom:8 }}>Now Playing</div>
        <NowPlayingCard
          track={ytNowPlaying}
          paused={paused}
          onPausePlay={handlePausePlay}
          onStop={handleStop}
        />
      </div>

      {/* YouTube search */}
      <div style={{ marginBottom:20 }}>
        <div style={{ fontSize:11, fontWeight:700, color:"#334155", letterSpacing:".07em", textTransform:"uppercase", marginBottom:10 }}>Search YouTube Music</div>
        <YouTubeMusicSearch />
      </div>

      {/* Other music services */}
      <div style={{ paddingTop:16, borderTop:"1px solid rgba(255,255,255,.06)" }}>
        <MusicServiceConnect onConnect={handleConnect} connectedServices={connectedServices} />
      </div>
    </div>
  );
}

// ─── Livato AI Assistant ─────────────────────────────────────────────────────
function LiveatoAI({ onClose, haDevices = [] }) {
  const [messages, setMessages] = useState([
    { role:"assistant", content:"Hi! I'm Livato AI. I can control your lights, locks, and more — just ask. 🏠" }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior:"smooth" }); }, [messages]);

  const send = async (text) => {
    const msg = text || input.trim();
    if (!msg || loading) return;
    setInput("");
    const userMsg = { role:"user", content: msg };
    const next = [...messages, userMsg];
    setMessages(next);
    setLoading(true);
    try {
      const res = await fetch("https://livato-server-production.up.railway.app/ai/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          messages: next.map(m => ({ role: m.role, content: m.content })),
          haDevices: haDevices
        }),
      });
      if (!res.ok) throw new Error("Server error " + res.status);
      const data = await res.json();
      setMessages(m => [...m, { role:"assistant", content: data.reply || "Done!" }]);
    } catch(err) {
      console.error("AI error:", err);
      setMessages(m => [...m, { role:"assistant", content:"Couldn't reach the server. Please try again! 🔌" }]);
    }
    setLoading(false);
  };

  const QUICK = ["Turn off all lights","Lock the front door","What's the weather?","Which devices are on?","Set movie scene","Turn on bedroom light"];

  return (
    <div style={{ background:"rgba(255,255,255,.04)",border:"1.5px solid rgba(93,184,50,.3)",borderRadius:20,padding:16,marginBottom:12 }}>
      <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12 }}>
        <div style={{ display:"flex",alignItems:"center",gap:8 }}>
          <div style={{ width:28,height:28,borderRadius:10,background:"linear-gradient(135deg,#5db832,#3d7a1a)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14 }}>🤖</div>
          <span style={{ color:"#a8e070",fontWeight:700,fontSize:13 }}>Livato AI</span>
          <div style={{ width:6,height:6,borderRadius:"50%",background:loading?"#f59e0b":"#22c55e",boxShadow:`0 0 5px ${loading?"#f59e0b":"#22c55e"}` }} />
          <span style={{ fontSize:10,color:"#475569" }}>{loading?"thinking…":"ready"}</span>
        </div>
        <button onClick={onClose} style={{ background:"none",border:"none",color:"#334155",cursor:"pointer",fontSize:18 }}>✕</button>
      </div>
      <div style={{ maxHeight:280,overflowY:"auto",display:"flex",flexDirection:"column",gap:10,paddingBottom:8 }}>
        {messages.map((m,i) => (
          <div key={i} style={{ display:"flex",justifyContent:m.role==="user"?"flex-end":"flex-start" }}>
            <div style={{ maxWidth:"82%",padding:"10px 14px",borderRadius:m.role==="user"?"16px 16px 4px 16px":"16px 16px 16px 4px",background:m.role==="user"?"linear-gradient(135deg,#5db832,#3d7a1a)":"rgba(255,255,255,.06)",border:m.role==="user"?"none":"1.5px solid rgba(255,255,255,.09)",color:"#f1f5f9",fontSize:13,lineHeight:1.5 }}>
              {m.content}
            </div>
          </div>
        ))}
        {loading && (
          <div style={{ display:"flex",gap:5,padding:"10px 14px",background:"rgba(255,255,255,.06)",border:"1.5px solid rgba(255,255,255,.09)",borderRadius:"16px 16px 16px 4px",width:"fit-content",alignItems:"center" }}>
            {[0,1,2].map(i => <div key={i} style={{ width:7,height:7,borderRadius:"50%",background:"#5db832",animation:`dot .9s ${i*.15}s ease-in-out infinite` }} />)}
          </div>
        )}
        <div ref={endRef} />
      </div>
      {messages.length <= 2 && (
        <div style={{ display:"flex",flexWrap:"wrap",gap:6,marginBottom:10 }}>
          {QUICK.map(q => (
            <button key={q} onClick={() => send(q)} style={{ padding:"6px 12px",borderRadius:20,border:"1.5px solid rgba(93,184,50,.3)",background:"rgba(93,184,50,.08)",color:"#a8e070",fontSize:11,fontWeight:600,cursor:"pointer" }}>{q}</button>
          ))}
        </div>
      )}
      <div style={{ display:"flex",gap:8,alignItems:"center" }}>
        <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key==="Enter" && send()}
          placeholder={loading?"Livato is thinking…":"Ask Livato AI anything…"}
          disabled={loading}
          style={{ flex:1,padding:"11px 14px",borderRadius:14,border:"1.5px solid rgba(255,255,255,.1)",background:"rgba(255,255,255,.06)",color:"#f1f5f9",fontSize:13,fontFamily:"inherit",outline:"none" }} />
        <button onClick={() => send()} disabled={!input.trim()||loading}
          style={{ width:40,height:40,borderRadius:"50%",background:input.trim()&&!loading?"#5db832":"rgba(255,255,255,.06)",border:"none",cursor:"pointer",fontSize:18,transition:"background .2s" }}>↑</button>
      </div>
    </div>
  );
}
// ─── Home Screen ─────────────────────────────────────────────────────────────
function MiniStat({ icon, val, alert }) {
  return <div style={{ flex:1,background:alert?"rgba(239,68,68,.1)":"rgba(255,255,255,.04)",borderRadius:8,padding:"5px 6px",textAlign:"center" }}><div style={{ fontSize:12 }}>{icon}</div><div style={{ color:alert?"#f87171":"#64748b",fontSize:10,fontWeight:700,marginTop:1 }}>{val}</div></div>;
}

function HomeScreen({ automations, onWeatherUpdate, haDevices, setHaDevices, scenes }) {
  // ── Real Weather ─────────────────────────────────────────────────
  const [tempUnit, setTempUnit] = useState(
    localStorage.getItem('livato_temp_unit') || 
    (navigator.language === 'en-US' ? 'F' : 'C')
  );
  const toTemp = (c) => tempUnit === 'F' 
    ? `${Math.round(c * 9/5 + 32)}°F` 
    : `${Math.round(c)}°C`;
  const [weather, setWeather] = useState(null);

  useEffect(() => {
    const fetchWeather = (lat, lon) => {
      fetch(`https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,relative_humidity_2m,precipitation_probability,wind_speed_10m,weather_code&daily=temperature_2m_max,temperature_2m_min,precipitation_probability_max&timezone=auto&forecast_days=7`)
        .then(res => res.json())
        .then(data => {
          const c = data.current;
          const codes = {0:"Clear Sky",1:"Mostly Clear",2:"Partly Cloudy",3:"Overcast",45:"Foggy",51:"Light Drizzle",53:"Drizzle",61:"Light Rain",63:"Rain",65:"Heavy Rain",71:"Light Snow",73:"Snow",80:"Rain Showers",95:"Thunderstorm",99:"Hail Storm"};
          const icons = {0:"☀️",1:"🌤",2:"⛅",3:"☁️",45:"🌫",51:"🌦",53:"🌦",61:"🌧",63:"🌧",65:"🌧",71:"🌨",73:"❄️",80:"🌦",95:"⛈",99:"⛈"};
          const weatherObj = {
            temp: Math.round(c.temperature_2m),
            condition: codes[c.weather_code] || "Clear",
            icon: icons[c.weather_code] || "☀️",
            humidity: c.relative_humidity_2m,
            wind: Math.round(c.wind_speed_10m),
            rain: c.precipitation_probability,
            forecast: data.daily.time.slice(0,7).map((day,i) => ({
              day: new Date(day).toLocaleDateString('en',{weekday:'short'}),
              high: Math.round(data.daily.temperature_2m_max[i]),
              low: Math.round(data.daily.temperature_2m_min[i]),
              rain: data.daily.precipitation_probability_max[i],
              icon: icons[data.daily.precipitation_probability_max[i] > 50 ? 61 : 0] || "☀️"
            }))
          };
          setWeather(weatherObj);
          if (onWeatherUpdate) onWeatherUpdate(weatherObj);
        })
        .catch(err => console.log("Weather error:", err));
    };
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        pos => fetchWeather(pos.coords.latitude, pos.coords.longitude),
        () => fetchWeather(37.1753, -113.0597)
      );
    } else {
      fetchWeather(37.1753, -113.0597);
    }
  }, []);

const toggleHADevice = (entityId, currentState) => {
  const domain = entityId.startsWith("light.") ? "light" : "switch";
  const service = currentState === "on" ? "turn_off" : "turn_on";
  fetch(`${LIVATO_SERVER}/ha/service`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({domain,service,entity_id:entityId})}).catch(()=>{});
  setHaDevices(prev => prev.map(d =>
    d.entity_id === entityId ? {...d, state: service==="turn_on"?"on":"off"} : d
  ));
};
  const [time, setTime]       = useState(new Date());
  const [showAI, setShowAI]   = useState(false);
  const [activeScene, setActiveScene] = useState(null);

  useEffect(() => { const t = setInterval(() => setTime(new Date()), 1000); return () => clearInterval(t); }, []);

  const hr = time.getHours();
  const greeting = hr < 12 ? "Good morning" : hr < 17 ? "Good afternoon" : "Good evening";
  const hh = String(time.getHours()).padStart(2,"0");
  const mm = String(time.getMinutes()).padStart(2,"0");
  const ss = String(time.getSeconds()).padStart(2,"0");
  const date = time.toLocaleDateString("en-US",{ weekday:"long",month:"long",day:"numeric" });
  const ambientColor = hr<6?"#1e1b4b":hr<9?"#f59e0b":hr<17?"#06b6d4":hr<20?"#f97316":"#5db832";

  return (
    <div>
      {/* Clock */}
      <div style={{ textAlign:"center",padding:"8px 0 20px",position:"relative" }}>
        <div style={{ position:"absolute",top:"50%",left:"50%",transform:"translate(-50%,-60%)",width:260,height:100,borderRadius:"50%",background:ambientColor,filter:"blur(60px)",opacity:.15,pointerEvents:"none" }} />
        <div style={{ color:"#334155",fontSize:13,fontWeight:500,marginBottom:4 }}>{date}</div>
        <div style={{ fontFamily:"Georgia,serif",fontSize:68,fontWeight:400,letterSpacing:"-2px",lineHeight:1,color:"#f8fafc",textShadow:`0 0 60px ${ambientColor}88` }}>
          {hh}<span style={{ opacity:.4,animation:"blink 1s step-end infinite" }}>:</span>{mm}
        </div>
        <div style={{ color:"#334155",fontSize:20,letterSpacing:".1em",marginTop:2 }}>{ss}</div>
        <div style={{ color:"#64748b",fontSize:14,marginTop:6 }}>{greeting}, welcome home 👋</div>

      </div>
      <div style={{ display:"flex",gap:4,background:"rgba(255,255,255,.06)",borderRadius:8,padding:2 }}>
  {['F','C'].map(u => (
    <button key={u} onClick={() => { setTempUnit(u); localStorage.setItem('livato_temp_unit',u); }}
      style={{ padding:"4px 10px",borderRadius:6,border:"none",cursor:"pointer",fontSize:11,fontWeight:700,
        background:tempUnit===u?"#5db832":"transparent",color:tempUnit===u?"#fff":"#475569" }}>
      °{u}
    </button>
  ))}
</div>

      {/* Weather + Status */}
      <div style={{ display:"flex",gap:8,marginBottom:12 }}>
        <div style={{ flex:1.4,background:"rgba(255,255,255,.04)",border:"1.5px solid rgba(255,255,255,.07)",borderRadius:18,padding:"13px 15px" }}>
          <div style={{ display:"flex",alignItems:"center",gap:10 }}>
            <span style={{ fontSize:32 }}>{weather?.icon ?? "⛅"}</span>
            <div>
              <div style={{ color:"#f1f5f9",fontWeight:800,fontSize:22 }}>{weather && toTemp ? toTemp(weather.temp) : (weather?.temp ?? "--")}°<span style={{ fontSize:13,fontWeight:400,color:"#64748b" }}></span></div>
              <div style={{ color:"#475569",fontSize:11 }}>{weather?.condition ?? "Loading..."}</div>
            </div>
          </div>
          <div style={{ display:"flex",gap:6,marginTop:10 }}>
            <MiniStat icon="🌧" val={`${weather?.rain ?? "--"}%`} />
            <MiniStat icon="💦" val={`${weather?.humidity ?? "--"}%`} />
            <MiniStat icon="💨" val={`${weather?.wind ?? "--"} ${tempUnit === 'F' ? 'mph' : 'km/h'}`} alert={(weather?.wind ?? 0) > 30} />
          </div>
        </div>
        <div style={{ flex:1,display:"flex",flexDirection:"column",gap:8 }}>
          <div style={{ flex:1,background:"rgba(34,197,94,.07)",border:"1px solid rgba(34,197,94,.2)",borderRadius:14,padding:"10px 12px",display:"flex",alignItems:"center",gap:8 }}>
            <div style={{ width:7,height:7,borderRadius:"50%",background:"#22c55e",boxShadow:"0 0 6px #22c55e" }} />
            <div><div style={{ color:"#4ade80",fontSize:11,fontWeight:700 }}>All secure</div><div style={{ color:"#1a3a24",fontSize:10 }}>Locks & sensors OK</div></div>
          </div>
          <div style={{ flex:1,background:"rgba(93,184,50,.07)",border:"1px solid rgba(93,184,50,.2)",borderRadius:14,padding:"10px 12px" }}>
            <div style={{ color:"#a8e070",fontSize:11,fontWeight:700 }}>⚡ {automations.filter(a=>a.enabled).length} automations</div>
            <div style={{ color:"#1a3010",fontSize:10 }}>Active right now</div>
          </div>
        </div>
      </div>

      {/* Scenes */}
      <div style={{ marginBottom:12 }}>
        <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8 }}>
          <div style={{ color:"#334155",fontSize:10,fontWeight:700,letterSpacing:".07em",textTransform:"uppercase" }}>Scenes</div>
          <div style={{ fontSize:9,color:"#1e293b",background:"rgba(255,255,255,.04)",padding:"2px 8px",borderRadius:5 }}>Tap to activate</div>
        </div>
        <div style={{ display:"flex",gap:7 }}>
          {(scenes||QUICK_SCENES).map(s => {
            const active = activeScene===s.id;
            return (
              <button key={s.id} onClick={() => setActiveScene(active?null:s.id)} style={{ flex:1,padding:"12px 6px",borderRadius:16,border:`1.5px solid ${active?s.color+"66":"rgba(255,255,255,.07)"}`,background:active?s.color+"15":"rgba(255,255,255,.03)",cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:5 }}>
                <span style={{ fontSize:22 }}>{s.icon}</span>
                <span style={{ color:active?s.color:"#475569",fontSize:11,fontWeight:700 }}>{s.label}</span>
              </button>
            );
          })}
        </div>
      </div>

{/* Real HA Devices */}
<div style={{ marginBottom:12 }}>
  <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8 }}>
    <div style={{ color:"#334155", fontSize:10, fontWeight:700, letterSpacing:".07em", textTransform:"uppercase" }}>My Devices</div>
    {haDevices.length > 0 && <span style={{ fontSize:10,color:"#475569" }}>{haDevices.filter(d=>d.state==="on").length} on · {haDevices.length} total</span>}
  </div>
  {haDevices.length === 0 ? (
    <div style={{ padding:"24px 16px",borderRadius:16,border:"1.5px dashed rgba(255,255,255,.1)",background:"rgba(255,255,255,.02)",textAlign:"center" }}>
      <div style={{ fontSize:32,marginBottom:10 }}>🏠</div>
      <div style={{ color:"#475569",fontSize:13,fontWeight:600,marginBottom:4 }}>No devices connected yet</div>
      <div style={{ color:"#334155",fontSize:11,marginBottom:16,lineHeight:1.6 }}>Connect your smart home devices to control them here. Make sure Home Assistant is running and devices are paired.</div>
      <div style={{ display:"flex",gap:8,justifyContent:"center",flexWrap:"wrap" }}>
        <button onClick={() => alert("Coming soon: Connect Philips Hue, Shelly, Nest, Ring and more via your account settings.")}
          style={{ padding:"8px 16px",borderRadius:10,border:"1.5px solid rgba(93,184,50,.3)",background:"rgba(93,184,50,.08)",color:"#5db832",fontSize:12,fontWeight:700,cursor:"pointer" }}>
          + Connect Devices
        </button>
        <button onClick={() => window.location.reload()}
          style={{ padding:"8px 16px",borderRadius:10,border:"1px solid rgba(255,255,255,.08)",background:"rgba(255,255,255,.04)",color:"#475569",fontSize:12,cursor:"pointer" }}>
          ↻ Refresh
        </button>
      </div>
    </div>
  ) : (
    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:7 }}>
      {haDevices.map(d => {
        const on = d.state === "on";
        const icon = d.entity_id.startsWith("light.") ? "💡" : d.entity_id.startsWith("climate.") ? "🌡️" : d.entity_id.startsWith("lock.") ? "🔒" : d.entity_id.startsWith("media_player.") ? "📺" : "🔌";
        return (
          <button key={d.entity_id} onClick={() => toggleHADevice(d.entity_id, d.state)}
            style={{ padding:"14px 10px", borderRadius:16, border:`1.5px solid ${on?"rgba(93,184,50,.3)":"rgba(255,255,255,.07)"}`, background:on?"rgba(93,184,50,.12)":"rgba(255,255,255,.03)", cursor:"pointer", display:"flex", flexDirection:"column", alignItems:"center", gap:6 }}>
            <span style={{ fontSize:24 }}>{icon}</span>
            <span style={{ color:on?"#5db832":"#475569", fontSize:10, fontWeight:700, textAlign:"center", lineHeight:1.3 }}>{d.attributes?.friendly_name||d.entity_id.split(".")[1]}</span>
            <span style={{ fontSize:9, color:on?"#64748b":"#1e293b", background:on?"rgba(93,184,50,.15)":"rgba(255,255,255,.04)", padding:"2px 7px", borderRadius:5 }}>{on?"On":"Off"}</span>
          </button>
        );
      })}
    </div>
  )}
</div>

      {/* Music */}
      <div style={{ marginBottom:12 }}>
        <div style={{ color:"#334155",fontSize:10,fontWeight:700,letterSpacing:".07em",textTransform:"uppercase",marginBottom:8 }}>Music</div>
        <MusicPlayer compact onGoToMusic={() => { const e = new CustomEvent("livato_goto_tab", {detail:"music"}); window.dispatchEvent(e); }} />
      </div>

      {/* AI Button */}
      {!showAI && (
        <button onClick={() => setShowAI(true)} style={{ width:"100%",padding:"16px 20px",borderRadius:20,background:"linear-gradient(135deg,rgba(93,184,50,.12),rgba(61,122,26,.1))",border:"1.5px solid rgba(93,184,50,.3)",cursor:"pointer",display:"flex",alignItems:"center",gap:14,marginBottom:12 }}>
          <div style={{ width:42,height:42,borderRadius:14,background:"linear-gradient(135deg,#5db832,#3d7a1a)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,boxShadow:"0 4px 16px rgba(93,184,50,.4)" }}>🤖</div>
          <div style={{ flex:1,textAlign:"left" }}>
            <div style={{ color:"#e2e8f0",fontWeight:700,fontSize:14 }}>Livato AI</div>
            <div style={{ color:"#475569",fontSize:12,marginTop:1 }}>"Turn off the porch light" · "What's the weather?" …</div>
          </div>
          <div style={{ color:"#5db832",fontSize:20 }}>🎙</div>
        </button>
      )}
      {showAI && <LiveatoAI onClose={() => setShowAI(false)} />}
    </div>
  );
}

// ─── Auto Card ───────────────────────────────────────────────────────────────
function AutoCard({ auto, onToggle, onDelete }) {
  const tDev = deviceById(auto.trigger.deviceId);
  const isWeather = tDev?.id===14;
  const [open, setOpen] = useState(false);
  return (
    <div style={{ background:"rgba(255,255,255,.035)",border:`1.5px solid ${isWeather?"rgba(93,184,50,.2)":"rgba(255,255,255,0.07)"}`,borderRadius:17,overflow:"hidden",borderLeft:isWeather?"3px solid #5db832":undefined }}>
      <div style={{ padding:"13px 14px",display:"flex",alignItems:"center",gap:10 }}>
        <div style={{ flex:1,minWidth:0 }}>
          <div style={{ display:"flex",alignItems:"center",gap:6,marginBottom:5 }}>
            <span style={{ color:"#f1f5f9",fontWeight:700,fontSize:13 }}>{auto.name}</span>
            {isWeather && <span style={{ fontSize:9,fontWeight:700,color:"#5db832",background:"rgba(93,184,50,.1)",padding:"1px 6px",borderRadius:5 }}>WEATHER</span>}
          </div>
          <div style={{ display:"flex",gap:5,flexWrap:"wrap" }}>
            {tDev && <Pill color="#6366f1">{tDev.icon} {TRIGGER_LABELS[auto.trigger.event]||auto.trigger.event}</Pill>}
            {auto.condition?.useTime && <Pill color="#06b6d4">🕐 {auto.condition.timeFrom}–{auto.condition.timeTo}</Pill>}
            {auto.actions.map((a,i) => a.type==="notify" ? <Pill key={i} color="#f59e0b">🔔 Notify</Pill> : <Pill key={i} color="#f59e0b">{deviceById(a.deviceId)?.icon} {ACTION_LABELS[a.action]}</Pill>)}
          </div>
        </div>
        <div style={{ display:"flex",flexDirection:"column",alignItems:"flex-end",gap:6 }}>
          <Toggle on={auto.enabled} onChange={() => onToggle(auto.id)} />
          <button onClick={() => setOpen(o=>!o)} style={{ background:"none",border:"none",color:"#475569",cursor:"pointer",fontSize:11 }}>{open?"▲":"▼"}</button>
        </div>
      </div>
      {open && (
        <div style={{ borderTop:"1px solid rgba(255,255,255,.06)",padding:"10px 14px 12px",background:"rgba(0,0,0,.15)" }}>
          <div style={{ color:"#475569",fontSize:11,marginBottom:3 }}>Last triggered: {auto.lastTriggered}</div>
          <div style={{ color:"#475569",fontSize:11,marginBottom:8 }}>Days: {auto.condition?.days?.map(d=>DAYS[d]).join(", ")||"Every day"}</div>
          <button onClick={() => onDelete(auto.id)} style={{ background:"rgba(239,68,68,.1)",border:"1px solid rgba(239,68,68,.2)",color:"#f87171",borderRadius:8,padding:"4px 13px",fontSize:12,fontWeight:600,cursor:"pointer" }}>Delete</button>
        </div>
      )}
    </div>
  );
}

// ─── Floor Plan ──────────────────────────────────────────────────────────────
function FloorPlan() {
  const CELL=76, W=4*CELL, H=4*CELL;
  const [selected, setSelected] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [rooms, setRooms] = useState(() => {
    try { const s = localStorage.getItem("livato_rooms"); return s ? JSON.parse(s) : ROOMS; } catch { return ROOMS; }
  });
  const [showAddRoom, setShowAddRoom] = useState(false);
  const [newRoomName, setNewRoomName] = useState("");
  const ROOM_COLORS = ["#6366f1","#f59e0b","#10b981","#ec4899","#06b6d4","#78716c","#5db832","#f97316","#a78bfa","#e11d48"];
  const selDev = DEVICES_INIT.find(d=>d.id===selected);

  const saveRooms = (r) => { setRooms(r); localStorage.setItem("livato_rooms", JSON.stringify(r)); };

  const addRoom = () => {
    if (!newRoomName.trim()) return;
    const newRoom = { id: newRoomName.toLowerCase().replace(/\s+/g,"-"), label: newRoomName, x:0, y:0, w:2, h:1, color: ROOM_COLORS[rooms.length % ROOM_COLORS.length] };
    saveRooms([...rooms, newRoom]);
    setNewRoomName(""); setShowAddRoom(false);
  };

  const deleteRoom = (id) => saveRooms(rooms.filter(r => r.id !== id));

  return (
    <div style={{ fontFamily:"sans-serif" }}>
      <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12 }}>
        <div>
          <div style={{ fontSize:18,fontFamily:"Georgia,serif",fontWeight:400,color:"#f1f5f9" }}>Floor Plan</div>
          <div style={{ fontSize:13,color:"#475569" }}>Your home layout</div>
        </div>
        <button onClick={() => setEditMode(e=>!e)}
          style={{ padding:"8px 16px",borderRadius:10,border:`1.5px solid ${editMode?"rgba(93,184,50,.4)":"rgba(255,255,255,.1)"}`,background:editMode?"rgba(93,184,50,.1)":"rgba(255,255,255,.04)",color:editMode?"#5db832":"#94a3b8",fontSize:13,fontWeight:700,cursor:"pointer" }}>
          {editMode ? "✓ Done" : "✏️ Edit"}
        </button>
      </div>

      {editMode && (
        <div style={{ marginBottom:12,padding:"12px 14px",background:"rgba(93,184,50,.06)",border:"1.5px solid rgba(93,184,50,.2)",borderRadius:14 }}>
          <div style={{ color:"#a8e070",fontSize:12,fontWeight:700,marginBottom:10 }}>Edit Mode — Manage Rooms</div>
          <div style={{ display:"flex",flexDirection:"column",gap:6 }}>
            {rooms.map(r => (
              <div key={r.id} style={{ display:"flex",alignItems:"center",gap:10,padding:"8px 12px",background:"rgba(255,255,255,.04)",borderRadius:10,border:"1px solid rgba(255,255,255,.07)" }}>
                <div style={{ width:12,height:12,borderRadius:3,background:r.color,flexShrink:0 }} />
                <span style={{ flex:1,color:"#f1f5f9",fontSize:13,fontWeight:600 }}>{r.label}</span>
                <button onClick={() => deleteRoom(r.id)} style={{ background:"rgba(239,68,68,.08)",border:"1px solid rgba(239,68,68,.2)",borderRadius:6,padding:"3px 10px",color:"#f87171",fontSize:11,cursor:"pointer" }}>Remove</button>
              </div>
            ))}
          </div>
          {!showAddRoom ? (
            <button onClick={() => setShowAddRoom(true)} style={{ width:"100%",marginTop:10,padding:"8px",borderRadius:10,border:"1.5px dashed rgba(93,184,50,.3)",background:"rgba(93,184,50,.05)",color:"#5db832",fontSize:13,fontWeight:700,cursor:"pointer" }}>+ Add Room</button>
          ) : (
            <div style={{ display:"flex",gap:8,marginTop:10 }}>
              <input value={newRoomName} onChange={e=>setNewRoomName(e.target.value)} onKeyDown={e=>e.key==="Enter"&&addRoom()}
                placeholder="Room name (e.g. Office)" autoFocus
                style={{ flex:1,padding:"8px 12px",borderRadius:10,border:"1.5px solid rgba(93,184,50,.3)",background:"rgba(255,255,255,.06)",color:"#f1f5f9",fontSize:13,outline:"none",fontFamily:"inherit" }} />
              <button onClick={addRoom} style={{ padding:"8px 16px",borderRadius:10,border:"none",background:"#5db832",color:"#fff",fontWeight:700,fontSize:13,cursor:"pointer" }}>Add</button>
              <button onClick={()=>setShowAddRoom(false)} style={{ padding:"8px 12px",borderRadius:10,border:"1px solid rgba(255,255,255,.1)",background:"transparent",color:"#475569",fontSize:13,cursor:"pointer" }}>Cancel</button>
            </div>
          )}
        </div>
      )}

      <div style={{ overflowX:"auto",overflowY:"auto" }}>
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display:"block",borderRadius:16,cursor:editMode?"crosshair":"default" }}>
          {rooms.map(r => {
            const isSelected = editMode && selectedRoom === r.id;
            return (
              <g key={r.id} style={{ cursor:editMode?"move":"default" }}
                onClick={() => editMode && setSelectedRoom(r.id === selectedRoom ? null : r.id)}>
                <rect x={r.x*CELL+2} y={r.y*CELL+2} width={r.w*CELL-4} height={r.h*CELL-4} rx={10}
                  fill={r.color+(isSelected?"25":"12")} stroke={r.color+(isSelected?"cc":"55")} strokeWidth={isSelected?2.5:1.5}/>
                <text x={r.x*CELL+10} y={r.y*CELL+17} fill={r.color+"99"} fontSize={8.5} fontWeight={700} fontFamily="Georgia,serif">{r.label}</text>
                {isSelected && editMode && (
                  <text x={r.x*CELL+r.w*CELL/2} y={r.y*CELL+r.h*CELL/2+5} textAnchor="middle" fill={r.color} fontSize={9} fontWeight={700}>selected</text>
                )}
              </g>
            );
          })}
          {DEVICES_INIT.map(d=>{
            const px=d.x*CELL,py=d.y*CELL,color=rooms.find(r=>r.id===d.room)?.color||"#5db832",isSel=selected===d.id;
            return <g key={d.id} style={{cursor:"pointer"}} onClick={()=>!editMode&&setSelected(selected===d.id?null:d.id)}><circle cx={px} cy={py} r={isSel?17:12} fill={color+(isSel?"30":"15")} stroke={color+(isSel?"cc":"44")} strokeWidth={isSel?2:1}/><text x={px} y={py+5} textAnchor="middle" fontSize={isSel?14:11} style={{userSelect:"none"}}>{d.icon}</text><circle cx={px+9} cy={py-9} r={3.5} fill={d.online?"#22c55e":"#475569"} stroke="#080d1a" strokeWidth={1.5}/></g>;
          })}
        </svg>
      </div>
      {editMode && selectedRoom && (
        <div style={{ marginTop:12,padding:"12px 14px",background:"rgba(255,255,255,.04)",border:"1px solid rgba(255,255,255,.08)",borderRadius:12 }}>
          <div style={{ color:"#94a3b8",fontSize:12,fontWeight:700,marginBottom:8 }}>
            Move: {rooms.find(r=>r.id===selectedRoom)?.label}
          </div>
          <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:8 }}>
            {[["X Position","x",0,4,0.5],["Y Position","y",0,4,0.5],["Width","w",1,4,0.5],["Height","h",0.5,3,0.5]].map(([label,key,min,max,step]) => (
              <div key={key}>
                <div style={{ fontSize:11,color:"#64748b",marginBottom:4 }}>{label}: {rooms.find(r=>r.id===selectedRoom)?.[key]}</div>
                <input type="range" min={min} max={max} step={step}
                  value={rooms.find(r=>r.id===selectedRoom)?.[key] || 0}
                  onChange={e => {
                    const val = Number(e.target.value);
                    const updated = rooms.map(r => r.id === selectedRoom ? {...r, [key]: val} : r);
                    saveRooms(updated);
                  }}
                  style={{ width:"100%", accentColor:"#5db832" }} />
              </div>
            ))}
          </div>
        </div>
      )}

      {selDev && !editMode && (
        <div style={{ marginTop:13,background:"rgba(255,255,255,.05)",border:"1.5px solid rgba(255,255,255,.1)",borderRadius:18,padding:16 }}>
          <div style={{ display:"flex",alignItems:"center",gap:11,marginBottom:11 }}>
            <span style={{ fontSize:26 }}>{selDev.icon}</span>
            <div><div style={{ color:"#f1f5f9",fontWeight:700,fontSize:15 }}>{selDev.name}</div><div style={{ color:"#475569",fontSize:12 }}>{rooms.find(r=>r.id===selDev.room)?.label} · {selDev.type}</div></div>
            <div style={{ marginLeft:"auto",display:"flex",alignItems:"center",gap:5 }}><div style={{ width:7,height:7,borderRadius:"50%",background:selDev.online?"#22c55e":"#475569" }}/><span style={{ color:selDev.online?"#22c55e":"#475569",fontSize:11,fontWeight:600 }}>{selDev.online?"Online":"Offline"}</span></div>
          </div>
          <div style={{ display:"flex",gap:7,flexWrap:"wrap" }}>
            {selDev.capabilities.actions.map(a=><button key={a} style={{ padding:"7px 13px",borderRadius:9,border:"1.5px solid rgba(93,184,50,.3)",background:"rgba(93,184,50,.08)",color:"#5db832",fontSize:12,fontWeight:600,cursor:"pointer" }}>{ACTION_LABELS[a]}</button>)}
            {!selDev.capabilities.actions.length && <div style={{ color:"#334155",fontSize:12 }}>Sensor only — read only device</div>}
          </div>
          {selDev.brightness!==undefined && <div style={{ marginTop:11 }}><div style={{ color:"#475569",fontSize:11,marginBottom:5 }}>Brightness — {selDev.brightness}%</div><div style={{ height:5,borderRadius:3,background:"rgba(255,255,255,.08)",overflow:"hidden" }}><div style={{ height:"100%",width:`${selDev.brightness}%`,background:"linear-gradient(90deg,#f59e0b,#fde68a)",borderRadius:3 }}/></div></div>}
        </div>
      )}
    </div>
  );
}

// ─── Weather Tab ─────────────────────────────────────────────────────────────
function WeatherTab({ onAddSuggestion, existingIds, weather, toTemp }) {
    const w = weather ?? { forecast:[], rain:0, humidity:0, wind:0, temp:0, condition:"Loading...", icon:"⛅" };
  
  const [thresholds, setThresholds] = useState(() => Object.fromEntries(WEATHER_TRIGGERS.map(t=>[t.id,t.default])));
  const nextRain = (w.forecast||[]).find(f=>f.rain>60);
  const sprinklerOk = !nextRain || nextRain.day!=="Today";
  if (!weather) return <div style={{ textAlign:"center",padding:"40px 20px",color:"#475569" }}><div style={{ fontSize:48,marginBottom:12 }}>🌤</div><div style={{ fontSize:14,fontWeight:600 }}>Loading weather data...</div><div style={{ fontSize:12,marginTop:6 }}>Make sure location permission is enabled</div></div>;
  return (
    <div>
      <div style={{ borderRadius:20,padding:"16px 18px",background:"linear-gradient(135deg,rgba(93,184,50,.09),rgba(6,182,212,.06))",border:"1.5px solid rgba(93,184,50,.18)",marginBottom:12 }}>
        <div style={{ display:"flex",alignItems:"flex-start",justifyContent:"space-between" }}>
          <div><div style={{ fontSize:40,lineHeight:1 }}>{weather?.icon ?? "⛅"}</div><div style={{ color:"#f1f5f9",fontWeight:800,fontSize:28,letterSpacing:"-1px",marginTop:4 }}>{weather && toTemp ? toTemp(weather.temp) : (weather?.temp ?? "--")}</div><div style={{ color:"#5db832",fontWeight:600,fontSize:13 }}>{weather?.condition ?? "Loading..."}</div><div style={{ color:"#475569",fontSize:11,marginTop:1 }}>Feels like {w.temp ? (toTemp ? toTemp(w.temp - 2) : (w.temp-2)+"°C") : "--"}</div></div>
          <div style={{ textAlign:"right" }}>
            <div style={{ background:sprinklerOk?"rgba(93,184,50,.12)":"rgba(239,68,68,.1)",border:`1px solid ${sprinklerOk?"rgba(93,184,50,.3)":"rgba(239,68,68,.25)"}`,borderRadius:12,padding:"8px 12px",marginBottom:7 }}><div style={{ fontSize:18 }}>💧</div><div style={{ color:sprinklerOk?"#5db832":"#f87171",fontWeight:700,fontSize:11 }}>{sprinklerOk?"Ready to water":"Skip watering"}</div><div style={{ color:"#475569",fontSize:10 }}>Next: Tue 06:00</div></div>
            <div style={{ color:"#475569",fontSize:11 }}>2 days skipped<br/><span style={{ color:"#5db832",fontWeight:600 }}>4.8k L saved</span></div>
          </div>
        </div>
        <div style={{ display:"flex",gap:6,marginTop:12 }}>
          {[["🌧",`${w.rain ?? "--"}%`,"Rain",false],["💦",`${weather?.humidity ?? "--"}%`,"Humidity",false],["☀️","--","UV",false],["💨",`${w.wind ?? "--"} km/h`,"Wind",(w.wind??0)>30]].map(([icon,val,label,alert])=>(
            <div key={label} style={{ flex:1,background:alert?"rgba(239,68,68,.1)":"rgba(255,255,255,.04)",border:`1px solid ${alert?"rgba(239,68,68,.25)":"rgba(255,255,255,.07)"}`,borderRadius:11,padding:"9px 8px",textAlign:"center" }}><div style={{ fontSize:16 }}>{icon}</div><div style={{ color:"#475569",fontSize:9,fontWeight:700,textTransform:"uppercase",marginTop:2 }}>{label}</div><div style={{ color:alert?"#f87171":"#f1f5f9",fontWeight:800,fontSize:14,marginTop:1 }}>{val}</div></div>
          ))}
        </div>
      </div>

      <div style={{ display:"flex",gap:5,overflowX:"auto",paddingBottom:4,marginBottom:12 }}>
        {(w.forecast||[]).map((f,i)=>(
          <div key={i} style={{ flexShrink:0,width:57,background:"rgba(255,255,255,.04)",border:`1.5px solid ${f.rain>60?"rgba(6,182,212,.3)":"rgba(255,255,255,.07)"}`,borderRadius:12,padding:"8px 6px",textAlign:"center" }}>
            <div style={{ color:f.rain>60?"#38bdf8":"#64748b",fontSize:9,fontWeight:700,marginBottom:3 }}>{f.day}</div>
            <div style={{ fontSize:17 }}>{f.icon}</div>
            <div style={{ color:"#f1f5f9",fontWeight:700,fontSize:12,marginTop:3 }}>{f.high}°</div>
            <div style={{ color:"#475569",fontSize:10 }}>{f.low}°</div>
            <div style={{ marginTop:3,fontSize:9,fontWeight:700,padding:"1px 3px",borderRadius:4,color:f.rain>60?"#38bdf8":"#334155",background:f.rain>60?"rgba(6,182,212,.1)":"transparent" }}>{f.rain}%</div>
          </div>
        ))}
      </div>

      {nextRain && <div style={{ padding:"9px 13px",borderRadius:11,background:"rgba(6,182,212,.07)",border:"1px solid rgba(6,182,212,.18)",display:"flex",alignItems:"center",gap:9,marginBottom:12 }}><span style={{ fontSize:18 }}>🌧</span><div><div style={{ color:"#38bdf8",fontWeight:600,fontSize:13 }}>Rain expected {nextRain.day} — {nextRain.rain}%</div><div style={{ color:"#475569",fontSize:11 }}>Sprinklers will skip automatically</div></div></div>}

      <div style={{ color:"#64748b",fontSize:10,fontWeight:700,letterSpacing:".07em",textTransform:"uppercase",marginBottom:9 }}>💡 Smart Sprinkler Automations</div>
      <div style={{ display:"flex",flexDirection:"column",gap:8,marginBottom:14 }}>
        {SMART_SUGGESTIONS.map(s=>{
          const added=existingIds.includes(s.id+"_weather");
          return (
            <div key={s.id} style={{ background:"rgba(255,255,255,.035)",border:"1.5px solid rgba(93,184,50,.15)",borderRadius:15,padding:"12px 14px",borderLeft:"3px solid #5db832" }}>
              <div style={{ display:"flex",gap:9 }}><span style={{ fontSize:20,flexShrink:0 }}>{s.icon}</span><div style={{ flex:1 }}><div style={{ color:"#f1f5f9",fontWeight:700,fontSize:13,marginBottom:2 }}>{s.title}</div><div style={{ color:"#64748b",fontSize:12,lineHeight:1.5,marginBottom:6 }}>{s.desc}</div><Pill color="#5db832">{s.savings}</Pill></div></div>
              <button onClick={()=>!added&&onAddSuggestion(s)} style={{ width:"100%",marginTop:10,padding:"7px",borderRadius:9,border:`1px solid ${added?"rgba(34,197,94,.25)":"rgba(93,184,50,.3)"}`,background:added?"rgba(34,197,94,.1)":"rgba(93,184,50,.12)",color:added?"#4ade80":"#5db832",fontWeight:700,fontSize:13,cursor:added?"default":"pointer" }}>{added?"✓ Added":"+ Add automation"}</button>
            </div>
          );
        })}
      </div>

      <div style={{ background:"rgba(255,255,255,.03)",border:"1.5px solid rgba(255,255,255,.07)",borderRadius:18,padding:16 }}>
        <div style={{ color:"#e2e8f0",fontWeight:700,fontSize:15,marginBottom:3 }}>⚙️ Thresholds</div>
        <div style={{ color:"#475569",fontSize:12,marginBottom:14 }}>Adjust when weather triggers fire</div>
        <div style={{ display:"flex",flexDirection:"column",gap:16 }}>
          {WEATHER_TRIGGERS.map(trigger=>(
            <div key={trigger.id}>
              <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6 }}>
                <div style={{ display:"flex",alignItems:"center",gap:7 }}><span style={{ fontSize:15 }}>{trigger.icon}</span><div style={{ color:"#e2e8f0",fontSize:13,fontWeight:600 }}>{trigger.label}</div></div>
                <span style={{ background:"rgba(93,184,50,.12)",border:"1px solid rgba(93,184,50,.25)",borderRadius:7,padding:"2px 9px",color:"#5db832",fontWeight:700,fontSize:13 }}>{thresholds[trigger.id]}{trigger.unit}</span>
              </div>
              <input type="range" min={trigger.min} max={trigger.max} value={thresholds[trigger.id]} onChange={e=>setThresholds(p=>({...p,[trigger.id]:Number(e.target.value)}))} style={{ width:"100%",accentColor:"#5db832" }}/>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Automation Builder ──────────────────────────────────────────────────────
function DevicePicker({ value, onChange, filterFn }) {
  return (
    <div style={{ display:"flex",flexDirection:"column",gap:7 }}>
      {DEVICES_INIT.filter(filterFn||(() => true)).map(d=>(
        <button key={d.id} onClick={()=>onChange(d.id)} style={{ display:"flex",alignItems:"center",gap:11,padding:"11px 13px",borderRadius:13,cursor:"pointer",textAlign:"left",border:"1.5px solid",borderColor:value===d.id?"#5db832":"rgba(255,255,255,0.07)",background:value===d.id?"rgba(93,184,50,.1)":"rgba(255,255,255,.03)" }}>
          <span style={{ fontSize:20 }}>{d.icon}</span>
          <div style={{ flex:1 }}><div style={{ color:"#e2e8f0",fontWeight:600,fontSize:13 }}>{d.name}</div><div style={{ color:"#475569",fontSize:11 }}>{roomById(d.room)?.label}</div></div>
          {d.id===14 && <span style={{ fontSize:9,fontWeight:700,color:"#5db832",background:"rgba(93,184,50,.1)",padding:"2px 6px",borderRadius:5 }}>WEATHER</span>}
          <div style={{ width:7,height:7,borderRadius:"50%",background:d.online?"#22c55e":"#475569" }}/>
        </button>
      ))}
    </div>
  );
}

function OptionPicker({ options, value, onChange }) {
  return (
    <div style={{ display:"flex",flexDirection:"column",gap:7 }}>
      {options.map(opt=>(
        <button key={opt.value} onClick={()=>onChange(opt.value)} style={{ padding:"10px 13px",borderRadius:11,cursor:"pointer",textAlign:"left",border:"1.5px solid",borderColor:value===opt.value?"#6366f1":"rgba(255,255,255,0.07)",background:value===opt.value?"rgba(99,102,241,.12)":"rgba(255,255,255,.03)",color:value===opt.value?"#a5b4fc":"#64748b",fontWeight:600,fontSize:13 }}>{opt.label}</button>
      ))}
    </div>
  );
}

function ActionBuilder({ actions, onChange }) {
  const add = type => onChange([...actions, type==="notify"?{type:"notify",message:""}:{type:"device",deviceId:null,action:null}]);
  const remove = i => onChange(actions.filter((_,j)=>j!==i));
  const update = (i,p) => onChange(actions.map((a,j)=>j===i?{...a,...p}:a));
  return (
    <div>
      <div style={{ display:"flex",flexDirection:"column",gap:9 }}>
        {actions.map((a,i)=>(
          <div key={i} style={{ background:"rgba(255,255,255,.04)",border:"1.5px solid rgba(255,255,255,.09)",borderRadius:14,padding:13 }}>
            <div style={{ display:"flex",justifyContent:"space-between",marginBottom:9 }}>
              <span style={{ fontSize:11,fontWeight:700,color:"#64748b",textTransform:"uppercase" }}>{a.type==="notify"?"🔔 Notification":"⚡ Device Action"}</span>
              <button onClick={()=>remove(i)} style={{ background:"none",border:"none",color:"#475569",cursor:"pointer",fontSize:16 }}>×</button>
            </div>
            {a.type==="device" && (<><DevicePicker value={a.deviceId} onChange={id=>update(i,{deviceId:id,action:null})} filterFn={d=>d.capabilities.actions.length>0}/>{a.deviceId && <div style={{ marginTop:9 }}><div style={{ color:"#64748b",fontSize:12,fontWeight:600,marginBottom:6 }}>What should it do?</div><OptionPicker value={a.action} onChange={v=>update(i,{action:v})} options={deviceById(a.deviceId).capabilities.actions.map(ac=>({value:ac,label:ACTION_LABELS[ac]}))}/></div>}</>)}
            {a.type==="notify" && <input value={a.message} onChange={e=>update(i,{message:e.target.value})} placeholder="Notification message…" style={{ width:"100%",padding:"9px 12px",borderRadius:10,border:"1.5px solid rgba(255,255,255,.1)",background:"rgba(255,255,255,.05)",color:"#f1f5f9",fontSize:13,fontFamily:"inherit",outline:"none",boxSizing:"border-box" }}/>}
          </div>
        ))}
      </div>
      <div style={{ display:"flex",gap:8,marginTop:10 }}>
        <button onClick={()=>add("device")} style={{ flex:1,padding:"9px",borderRadius:10,cursor:"pointer",border:"1.5px dashed rgba(93,184,50,.35)",background:"rgba(93,184,50,.06)",color:"#5db832",fontWeight:600,fontSize:13 }}>+ Device</button>
        <button onClick={()=>add("notify")} style={{ flex:1,padding:"9px",borderRadius:10,cursor:"pointer",border:"1.5px dashed rgba(14,165,233,.35)",background:"rgba(14,165,233,.06)",color:"#38bdf8",fontWeight:600,fontSize:13 }}>+ Notify</button>
      </div>
    </div>
  );
}

// ─── Main App ────────────────────────────────────────────────────────────────
// ─── Device Discovery Modal ───────────────────────────────────────────────────
function DeviceDiscovery({ onClose }) {
  const [scanning, setScanning] = useState(true);
  const [discovered, setDiscovered] = useState([]);
  const [added, setAdded] = useState([]);
  const [filter, setFilter] = useState("all");
  const ICONS = { light:"💡", switch:"🔌", lock:"🔒", climate:"🌡️", media_player:"📺", sensor:"👁️", binary_sensor:"🚨", cover:"🪟", fan:"🌀" };
  const scan = async () => {
    setScanning(true);
    try {
      const res = await fetch(`${LIVATO_SERVER}/ha/states`);
      const data = await res.json();
      if (Array.isArray(data)) {
        setDiscovered(data.filter(d => Object.keys(ICONS).some(t => d.entity_id.startsWith(t+"."))).map(d => ({
          entity_id: d.entity_id, name: d.attributes?.friendly_name || d.entity_id.split(".")[1].replace(/_/g," "),
          state: d.state, type: d.entity_id.split(".")[0], icon: ICONS[d.entity_id.split(".")[0]] || "🔧",
        })));
      }
    } catch { setDiscovered([]); }
    setScanning(false);
  };
  useEffect(() => { scan(); }, []);
  const categories = [...new Set(discovered.map(d => d.type))];
  const filtered = filter === "all" ? discovered : discovered.filter(d => d.type === filter);
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.85)", backdropFilter:"blur(10px)", zIndex:500, display:"flex", alignItems:"flex-end", justifyContent:"center" }}>
      <div style={{ width:"100%", maxWidth:480, background:"#0c1a08", borderRadius:"24px 24px 0 0", border:"1.5px solid rgba(255,255,255,.1)", maxHeight:"90vh", display:"flex", flexDirection:"column", overflow:"hidden" }}>
        <div style={{ padding:"20px 20px 12px", display:"flex", alignItems:"center", justifyContent:"space-between", flexShrink:0 }}>
          <div>
            <div style={{ color:"#f1f5f9", fontWeight:700, fontSize:17 }}>Device Discovery</div>
            <div style={{ color:"#475569", fontSize:12, marginTop:2 }}>{scanning ? "Scanning Home Assistant…" : `${discovered.length} devices found`}</div>
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <button onClick={scan} style={{ padding:"6px 12px", borderRadius:10, border:"1.5px solid rgba(93,184,50,.3)", background:"rgba(93,184,50,.08)", color:"#5db832", fontSize:12, fontWeight:700, cursor:"pointer" }}>↻ Scan</button>
            <button onClick={onClose} style={{ width:32, height:32, borderRadius:"50%", background:"rgba(255,255,255,.06)", border:"1px solid rgba(255,255,255,.1)", cursor:"pointer", color:"#475569", fontSize:16 }}>✕</button>
          </div>
        </div>
        {scanning && <div style={{ padding:"0 20px 12px", flexShrink:0 }}><div style={{ height:3, borderRadius:2, background:"rgba(255,255,255,.06)", overflow:"hidden" }}><div style={{ height:"100%", width:"60%", background:"linear-gradient(90deg,transparent,#5db832,transparent)", animation:"scanbar 1.5s ease-in-out infinite" }} /></div></div>}
        {!scanning && discovered.length > 0 && (
          <div style={{ padding:"0 20px 12px", display:"flex", gap:6, overflowX:"auto", flexShrink:0 }}>
            {["all",...categories].map(c => (
              <button key={c} onClick={() => setFilter(c)} style={{ padding:"5px 12px", borderRadius:20, border:`1.5px solid ${filter===c?"rgba(93,184,50,.4)":"rgba(255,255,255,.07)"}`, background:filter===c?"rgba(93,184,50,.1)":"rgba(255,255,255,.03)", color:filter===c?"#5db832":"#475569", fontSize:11, fontWeight:700, cursor:"pointer", flexShrink:0, textTransform:"capitalize" }}>
                {ICONS[c]||"🏠"} {c==="all"?"All":c.replace("_"," ")} ({c==="all"?discovered.length:discovered.filter(d=>d.type===c).length})
              </button>
            ))}
          </div>
        )}
        <div style={{ overflowY:"auto", padding:"0 20px 20px", display:"flex", flexDirection:"column", gap:8 }}>
          {!scanning && filtered.length === 0 && <div style={{ textAlign:"center", padding:"40px 20px", color:"#475569" }}><div style={{ fontSize:40, marginBottom:12 }}>🔍</div><div style={{ fontWeight:600, fontSize:14 }}>No devices found</div><div style={{ fontSize:12, marginTop:6 }}>Make sure your HA URL and token are set in Settings (tap your avatar).</div></div>}
          {filtered.map(d => {
            const isAdded = added.includes(d.entity_id);
            return (
              <div key={d.entity_id} style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 14px", borderRadius:16, border:`1.5px solid ${isAdded?"rgba(93,184,50,.3)":"rgba(255,255,255,.07)"}`, background:isAdded?"rgba(93,184,50,.06)":"rgba(255,255,255,.03)" }}>
                <div style={{ width:42, height:42, borderRadius:12, background:"rgba(93,184,50,.08)", border:"1.5px solid rgba(93,184,50,.15)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, flexShrink:0 }}>{d.icon}</div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ color:"#f1f5f9", fontWeight:700, fontSize:13, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{d.name}</div>
                  <div style={{ color:"#334155", fontSize:11, marginTop:1 }}>{d.entity_id}</div>
                </div>
                <button onClick={() => !isAdded && setAdded(p=>[...p,d.entity_id])} style={{ padding:"6px 14px", borderRadius:10, border:`1.5px solid ${isAdded?"rgba(34,197,94,.3)":"rgba(93,184,50,.35)"}`, background:isAdded?"rgba(34,197,94,.1)":"rgba(93,184,50,.1)", color:isAdded?"#4ade80":"#5db832", fontSize:12, fontWeight:700, cursor:isAdded?"default":"pointer", flexShrink:0 }}>{isAdded?"✓ Added":"+ Add"}</button>
              </div>
            );
          })}
          <div style={{ marginTop:8, padding:"14px 16px", borderRadius:14, border:"1.5px dashed rgba(255,255,255,.08)", background:"rgba(255,255,255,.02)" }}>
            <div style={{ color:"#334155", fontSize:11, fontWeight:700, marginBottom:8, textTransform:"uppercase", letterSpacing:".06em" }}>⚙️ Coming soon</div>
            <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>{["Zigbee","Z-Wave","Matter","Thread","Bluetooth","Tuya Cloud","Shelly Cloud"].map(p=><span key={p} style={{ padding:"4px 10px", borderRadius:8, background:"rgba(255,255,255,.04)", border:"1px solid rgba(255,255,255,.06)", color:"#334155", fontSize:11 }}>{p}</span>)}</div>
          </div>
        </div>
      </div>
      <style>{`@keyframes scanbar{0%{transform:translateX(-200%)}100%{transform:translateX(400%)}}`}</style>
    </div>
  );
}

// ─── Devices Tab ──────────────────────────────────────────────────────────────
function DevicesTab({ haDevices, onToggle, onRefresh }) {
  const [filter, setFilter]       = useState("all");
  const [search, setSearch]       = useState("");
  const [renames, setRenames]     = useState(() => { try { return JSON.parse(localStorage.getItem("livato_renames")||"{}"); } catch { return {}; } });
  const [hidden, setHidden]       = useState(() => { try { return JSON.parse(localStorage.getItem("livato_hidden")||"[]"); } catch { return []; } });
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName]   = useState("");
  const [showDiscovery, setShowDiscovery] = useState(false);
  const [amazonStatus, setAmazonStatus]   = useState({ connected:false, profile:null, loading:true });
  const [amazonDevices, setAmazonDevices] = useState([]);
  const [hubDevices, setHubDevices]       = useState([]);
  const [googleStatus, setGoogleStatus]   = useState({ connected:false, loading:true });
  const token = localStorage.getItem("livato_token");

  // Check integration statuses on mount + handle OAuth return
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("amazon") === "connected") { window.history.replaceState({}, "", "/"); }
    if (params.get("google") === "connected") { window.history.replaceState({}, "", "/"); }

    const headers = { "Authorization": `Bearer ${token}` };
    fetch(`${LIVATO_SERVER}/auth/amazon/status`, { headers })
      .then(r=>r.json()).then(d => {
        setAmazonStatus({ connected: d.connected, profile: d.profile, loading: false });
        if (d.connected) loadAmazonDevices(headers);
      }).catch(() => setAmazonStatus({ connected:false, profile:null, loading:false }));

    // Load hub devices (saved during Alexa discovery)
    fetch(`${LIVATO_SERVER}/hub/devices`, { headers })
      .then(r=>r.json()).then(d => setHubDevices(d.devices || []))
      .catch(() => {});

    fetch(`${LIVATO_SERVER}/auth/google/status`, { headers })
      .then(r=>r.json()).then(d => setGoogleStatus({ connected: d.connected, loading: false }))
      .catch(() => setGoogleStatus({ connected:false, loading:false }));
  }, []);

  const loadAmazonDevices = (headers) => {
    fetch(`${LIVATO_SERVER}/auth/amazon/devices`, { headers: headers || { "Authorization": `Bearer ${token}` } })
      .then(r=>r.json()).then(d => setAmazonDevices(d.devices || []))
      .catch(() => {});
  };

  const connectAmazon = () => {
  const token = localStorage.getItem("livato_token");
  window.location.href = `${LIVATO_SERVER}/auth/amazon?token=${token}`;
};
  const connectGoogle = () => {
    const token = localStorage.getItem("livato_token");
    window.location.href = `${LIVATO_SERVER}/auth/google?token=${token}`;
  };
  const disconnectAmazon = async () => {
    await fetch(`${LIVATO_SERVER}/auth/amazon/disconnect`, { headers: { "Authorization": `Bearer ${token}` } });
    setAmazonStatus({ connected:false, profile:null, loading:false });
    setAmazonDevices([]);
  };

  const saveRename  = (id, name) => { const u={...renames,[id]:name}; setRenames(u); localStorage.setItem("livato_renames",JSON.stringify(u)); setEditingId(null); };
  const hideDevice  = id => { const u=[...hidden,id]; setHidden(u); localStorage.setItem("livato_hidden",JSON.stringify(u)); };
  const getIcon     = id => id.startsWith("light.")?"💡":id.startsWith("switch.")?"🔌":id.startsWith("lock.")?"🔒":id.startsWith("climate.")?"🌡️":id.startsWith("media_player.")?"📺":"🔧";
  const getAmazonIcon = type => ({ LIGHT:"💡", SWITCH:"🔌", THERMOSTAT:"🌡️", LOCK:"🔒", TV:"📺", SMARTPLUG:"🔌", SPEAKER:"🔊", CAMERA:"📷" }[type] || "🏠");

  const FILTERS = ["all","light","switch","lock","climate","media_player","amazon","hub"];
  const visible = haDevices
    .filter(d => !hidden.includes(d.entity_id))
    .filter(d => filter==="all" || filter==="amazon" ? true : d.entity_id.startsWith(filter+"."))
    .filter(d => { const name=(renames[d.entity_id]||d.attributes?.friendly_name||d.entity_id).toLowerCase(); return name.includes(search.toLowerCase()); });

  const visibleAmazon = filter==="all"||filter==="amazon" ? amazonDevices.filter(d=>{
    const name = (renames["az_"+d.id]||d.name).toLowerCase();
    return name.includes(search.toLowerCase());
  }) : [];

  const totalDevices = haDevices.length + amazonDevices.length + hubDevices.length;

  return (
    <div style={{ fontFamily:"sans-serif" }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
        <div>
          <div style={{ fontSize:18, fontFamily:"Georgia,serif", fontWeight:400, color:"#f1f5f9" }}>Devices</div>
          <div style={{ fontSize:12, color:"#475569", marginTop:2 }}>{totalDevices} connected · {haDevices.filter(d=>d.state==="on").length + visibleAmazon.length} active</div>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <button onClick={onRefresh} style={{ padding:"7px 12px", borderRadius:10, border:"1px solid rgba(255,255,255,.1)", background:"rgba(255,255,255,.04)", color:"#475569", fontSize:12, cursor:"pointer" }}>↻</button>
          <button onClick={()=>setShowDiscovery(true)} style={{ padding:"7px 14px", borderRadius:10, border:"1.5px solid rgba(93,184,50,.3)", background:"rgba(93,184,50,.08)", color:"#5db832", fontSize:12, fontWeight:700, cursor:"pointer" }}>+ Add</button>
        </div>
      </div>

      {/* Cloud service connect cards */}
      <div style={{ marginBottom:14 }}>
        {/* Amazon Alexa */}
        <div style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 14px", borderRadius:16,
          border:`1.5px solid ${amazonStatus.connected?"rgba(0,168,225,.3)":"rgba(255,255,255,.08)"}`,
          background:amazonStatus.connected?"rgba(0,168,225,.06)":"rgba(255,255,255,.03)", marginBottom:8 }}>
          <div style={{ width:40, height:40, borderRadius:12, background:"rgba(0,168,225,.12)", border:"1.5px solid rgba(0,168,225,.2)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, flexShrink:0 }}>🔵</div>
          <div style={{ flex:1 }}>
            <div style={{ color:"#f1f5f9", fontWeight:700, fontSize:13 }}>Amazon Alexa</div>
            <div style={{ color:"#475569", fontSize:11, marginTop:1 }}>
              {amazonStatus.loading ? "Checking…" : amazonStatus.connected ? `✓ Connected${amazonStatus.profile?.name ? ` · ${amazonStatus.profile.name}` : ""} · ${amazonDevices.length} devices` : "Connect to see your Echo and Alexa devices"}
            </div>
          </div>
          {!amazonStatus.loading && (
            amazonStatus.connected
              ? <button onClick={disconnectAmazon} style={{ padding:"6px 12px", borderRadius:9, border:"1px solid rgba(239,68,68,.2)", background:"rgba(239,68,68,.06)", color:"#f87171", fontSize:11, cursor:"pointer" }}>Disconnect</button>
              : <button onClick={connectAmazon} style={{ padding:"7px 14px", borderRadius:9, border:"1.5px solid rgba(0,168,225,.4)", background:"rgba(0,168,225,.12)", color:"#00a8e1", fontSize:12, fontWeight:700, cursor:"pointer" }}>Connect</button>
          )}
        </div>

        {/* Google Home */}
        <div style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 14px", borderRadius:16,
          border:`1.5px solid ${googleStatus.connected?"rgba(66,133,244,.3)":"rgba(255,255,255,.08)"}`,
          background:googleStatus.connected?"rgba(66,133,244,.06)":"rgba(255,255,255,.03)", marginBottom:8 }}>
          <div style={{ width:40, height:40, borderRadius:12, background:"rgba(66,133,244,.12)", border:"1.5px solid rgba(66,133,244,.2)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, flexShrink:0 }}>🏠</div>
          <div style={{ flex:1 }}>
            <div style={{ color:"#f1f5f9", fontWeight:700, fontSize:13 }}>Google Home</div>
            <div style={{ color:"#475569", fontSize:11, marginTop:1 }}>
              {googleStatus.loading ? "Checking…" : googleStatus.connected ? "✓ Connected" : "⚙️ Add GOOGLE_CLIENT_ID to Railway to enable"}
            </div>
          </div>
          {!googleStatus.loading && !googleStatus.connected && (
            <div style={{ fontSize:11, padding:"4px 10px", borderRadius:8, background:"rgba(255,255,255,.04)", border:"1px solid rgba(255,255,255,.07)", color:"#334155" }}>Soon</div>
          )}
        </div>

        {/* Home Assistant */}
        <div style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 14px", borderRadius:16,
          border:`1.5px solid ${haDevices.length>0?"rgba(93,184,50,.25)":"rgba(255,255,255,.08)"}`,
          background:haDevices.length>0?"rgba(93,184,50,.05)":"rgba(255,255,255,.03)" }}>
          <div style={{ width:40, height:40, borderRadius:12, background:"rgba(93,184,50,.1)", border:"1.5px solid rgba(93,184,50,.2)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, flexShrink:0 }}>🤖</div>
          <div style={{ flex:1 }}>
            <div style={{ color:"#f1f5f9", fontWeight:700, fontSize:13 }}>Home Assistant</div>
            <div style={{ color:"#475569", fontSize:11, marginTop:1 }}>
              {haDevices.length > 0 ? `✓ Connected · ${haDevices.length} devices` : "Set HA URL + token in Settings (tap your avatar)"}
            </div>
          </div>
          <button onClick={onRefresh} style={{ padding:"6px 12px", borderRadius:9, border:"1px solid rgba(93,184,50,.2)", background:"rgba(93,184,50,.06)", color:"#5db832", fontSize:11, cursor:"pointer" }}>↻ Sync</button>
        </div>
      </div>

      {/* Search + filter */}
      <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search devices…"
        style={{ width:"100%", padding:"10px 14px", borderRadius:12, border:"1.5px solid rgba(255,255,255,.1)", background:"rgba(255,255,255,.06)", color:"#f1f5f9", fontSize:13, outline:"none", fontFamily:"inherit", boxSizing:"border-box", marginBottom:10 }}/>
      <div style={{ display:"flex", gap:6, overflowX:"auto", marginBottom:14, paddingBottom:2 }}>
        {FILTERS.map(f=>(
          <button key={f} onClick={()=>setFilter(f)} style={{ padding:"5px 12px", borderRadius:20, border:`1.5px solid ${filter===f?"rgba(93,184,50,.4)":"rgba(255,255,255,.07)"}`, background:filter===f?"rgba(93,184,50,.1)":"rgba(255,255,255,.03)", color:filter===f?"#5db832":"#475569", fontSize:11, fontWeight:700, cursor:"pointer", flexShrink:0, textTransform:"capitalize" }}>
            {f==="all"?"All":f==="media_player"?"Media":f==="amazon"?"Amazon":f}
            {f==="amazon" && amazonDevices.length>0 && <span style={{ marginLeft:4, background:"rgba(0,168,225,.2)", color:"#00a8e1", borderRadius:5, padding:"0 5px", fontSize:10 }}>{amazonDevices.length}</span>}
          </button>
        ))}
      </div>

      {/* Empty state */}
      {totalDevices === 0 && (
        <div style={{ textAlign:"center", padding:"40px 20px", border:"1.5px dashed rgba(255,255,255,.1)", borderRadius:16 }}>
          <div style={{ fontSize:40, marginBottom:12 }}>🏠</div>
          <div style={{ color:"#475569", fontWeight:700, fontSize:14, marginBottom:6 }}>No devices connected yet</div>
          <div style={{ color:"#334155", fontSize:12, lineHeight:1.6 }}>Connect Amazon Alexa above, or set your Home Assistant URL in Settings.</div>
        </div>
      )}

      {/* HA Devices */}
      <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
        {visible.map(d => {
          const on = d.state==="on"||d.state==="locked"||d.state==="playing";
          const displayName = renames[d.entity_id]||d.attributes?.friendly_name||d.entity_id.split(".")[1].replace(/_/g," ");
          const canToggle = d.entity_id.startsWith("light.")||d.entity_id.startsWith("switch.")||d.entity_id.startsWith("lock.");
          const isEditing = editingId === d.entity_id;
          return (
            <div key={d.entity_id} style={{ background:"rgba(255,255,255,.035)", border:`1.5px solid ${on?"rgba(93,184,50,.2)":"rgba(255,255,255,.07)"}`, borderRadius:16, padding:"12px 14px" }}>
              <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                <div style={{ width:42, height:42, borderRadius:12, background:on?"rgba(93,184,50,.12)":"rgba(255,255,255,.06)", border:`1.5px solid ${on?"rgba(93,184,50,.25)":"rgba(255,255,255,.1)"}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, flexShrink:0 }}>{getIcon(d.entity_id)}</div>
                <div style={{ flex:1, minWidth:0 }}>
                  {isEditing ? (
                    <div style={{ display:"flex", gap:6 }}>
                      <input value={editName} onChange={e=>setEditName(e.target.value)} onKeyDown={e=>e.key==="Enter"&&saveRename(d.entity_id,editName)} autoFocus style={{ flex:1, padding:"5px 9px", borderRadius:8, border:"1.5px solid rgba(93,184,50,.4)", background:"rgba(255,255,255,.08)", color:"#f1f5f9", fontSize:13, outline:"none", fontFamily:"inherit" }}/>
                      <button onClick={()=>saveRename(d.entity_id,editName)} style={{ padding:"5px 10px", borderRadius:8, border:"none", background:"#5db832", color:"#fff", fontSize:12, fontWeight:700, cursor:"pointer" }}>✓</button>
                      <button onClick={()=>setEditingId(null)} style={{ padding:"5px 10px", borderRadius:8, border:"1px solid rgba(255,255,255,.1)", background:"transparent", color:"#475569", fontSize:12, cursor:"pointer" }}>✕</button>
                    </div>
                  ) : (
                    <>
                      <div style={{ color:"#f1f5f9", fontWeight:700, fontSize:13 }}>{displayName}</div>
                      <div style={{ color:"#334155", fontSize:11, marginTop:1 }}>{d.entity_id} · <span style={{ color:on?"#5db832":"#475569" }}>{d.state}</span></div>
                    </>
                  )}
                </div>
                {canToggle && !isEditing && <Toggle on={on} onChange={()=>onToggle(d.entity_id, d.state)}/>}
              </div>
              {!isEditing && (
                <div style={{ display:"flex", gap:6, marginTop:10 }}>
                  <button onClick={()=>{setEditingId(d.entity_id);setEditName(displayName);}} style={{ padding:"5px 12px", borderRadius:8, border:"1px solid rgba(255,255,255,.08)", background:"rgba(255,255,255,.04)", color:"#64748b", fontSize:11, cursor:"pointer" }}>✏️ Rename</button>
                  <button onClick={()=>hideDevice(d.entity_id)} style={{ padding:"5px 12px", borderRadius:8, border:"1px solid rgba(239,68,68,.2)", background:"rgba(239,68,68,.06)", color:"#f87171", fontSize:11, cursor:"pointer" }}>Hide</button>
                </div>
              )}
            </div>
          );
        })}

        {/* Amazon Devices */}
        {visibleAmazon.map(d => {
          const displayName = renames["az_"+d.id] || d.name;
          const isEditing = editingId === "az_"+d.id;
          return (
            <div key={"az_"+d.id} style={{ background:"rgba(0,168,225,.04)", border:"1.5px solid rgba(0,168,225,.15)", borderRadius:16, padding:"12px 14px" }}>
              <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                <div style={{ width:42, height:42, borderRadius:12, background:"rgba(0,168,225,.1)", border:"1.5px solid rgba(0,168,225,.2)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, flexShrink:0 }}>{getAmazonIcon(d.type)}</div>
                <div style={{ flex:1, minWidth:0 }}>
                  {isEditing ? (
                    <div style={{ display:"flex", gap:6 }}>
                      <input value={editName} onChange={e=>setEditName(e.target.value)} onKeyDown={e=>e.key==="Enter"&&saveRename("az_"+d.id,editName)} autoFocus style={{ flex:1, padding:"5px 9px", borderRadius:8, border:"1.5px solid rgba(0,168,225,.4)", background:"rgba(255,255,255,.08)", color:"#f1f5f9", fontSize:13, outline:"none", fontFamily:"inherit" }}/>
                      <button onClick={()=>saveRename("az_"+d.id,editName)} style={{ padding:"5px 10px", borderRadius:8, border:"none", background:"#00a8e1", color:"#fff", fontSize:12, fontWeight:700, cursor:"pointer" }}>✓</button>
                      <button onClick={()=>setEditingId(null)} style={{ padding:"5px 10px", borderRadius:8, border:"1px solid rgba(255,255,255,.1)", background:"transparent", color:"#475569", fontSize:12, cursor:"pointer" }}>✕</button>
                    </div>
                  ) : (
                    <>
                      <div style={{ color:"#f1f5f9", fontWeight:700, fontSize:13 }}>{displayName}</div>
                      <div style={{ display:"flex", alignItems:"center", gap:6, marginTop:2 }}>
                        <span style={{ color:"#00a8e1", fontSize:10, fontWeight:700, background:"rgba(0,168,225,.1)", padding:"1px 6px", borderRadius:5 }}>ALEXA</span>
                        <span style={{ color:"#334155", fontSize:10 }}>{d.type.toLowerCase()}</span>
                      </div>
                    </>
                  )}
                </div>
                {!isEditing && (
                  <div style={{ display:"flex", gap:6 }}>
                    <button onClick={()=>{
                      const h = { "Authorization":`Bearer ${token}`, "Content-Type":"application/json" };
                      fetch(`${LIVATO_SERVER}/auth/amazon/control`, { method:"POST", headers:h, body:JSON.stringify({ endpointId:d.id, action:"turn_on" }) });
                    }} style={{ padding:"5px 10px", borderRadius:8, border:"1px solid rgba(0,168,225,.25)", background:"rgba(0,168,225,.1)", color:"#00a8e1", fontSize:11, cursor:"pointer" }}>On</button>
                    <button onClick={()=>{
                      const h = { "Authorization":`Bearer ${token}`, "Content-Type":"application/json" };
                      fetch(`${LIVATO_SERVER}/auth/amazon/control`, { method:"POST", headers:h, body:JSON.stringify({ endpointId:d.id, action:"turn_off" }) });
                    }} style={{ padding:"5px 10px", borderRadius:8, border:"1px solid rgba(255,255,255,.1)", background:"rgba(255,255,255,.04)", color:"#475569", fontSize:11, cursor:"pointer" }}>Off</button>
                  </div>
                )}
              </div>
              {!isEditing && (
                <div style={{ marginTop:10 }}>
                  <button onClick={()=>{setEditingId("az_"+d.id);setEditName(displayName);}} style={{ padding:"5px 12px", borderRadius:8, border:"1px solid rgba(255,255,255,.08)", background:"rgba(255,255,255,.04)", color:"#64748b", fontSize:11, cursor:"pointer" }}>✏️ Rename</button>
                </div>
              )}
            </div>
          );
        })}

        {/* Hub Devices — synced from Alexa/HA discovery */}
        {(filter==="all"||filter==="hub") && hubDevices.filter(d=>{
          const n=(renames["hub_"+d.device_id]||d.name||"").toLowerCase();
          return n.includes(search.toLowerCase());
        }).map(d => {
          const on = d.state==="on"||d.state==="locked"||d.state==="playing";
          const displayName = renames["hub_"+d.device_id] || d.name || d.device_id;
          const isEditing = editingId === "hub_"+d.device_id;
          const ICONS = { light:"💡", switch:"🔌", lock:"🔒", climate:"🌡️", media_player:"📺", fan:"🌀", cover:"🪟" };
          return (
            <div key={"hub_"+d.device_id} style={{ background:"rgba(93,184,50,.04)", border:`1.5px solid ${on?"rgba(93,184,50,.25)":"rgba(255,255,255,.07)"}`, borderRadius:16, padding:"12px 14px" }}>
              <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                <div style={{ width:42, height:42, borderRadius:12, background:on?"rgba(93,184,50,.12)":"rgba(255,255,255,.06)", border:`1.5px solid ${on?"rgba(93,184,50,.25)":"rgba(255,255,255,.1)"}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, flexShrink:0 }}>{ICONS[d.type]||"🔧"}</div>
                <div style={{ flex:1, minWidth:0 }}>
                  {isEditing ? (
                    <div style={{ display:"flex", gap:6 }}>
                      <input value={editName} onChange={e=>setEditName(e.target.value)} onKeyDown={e=>e.key==="Enter"&&saveRename("hub_"+d.device_id,editName)} autoFocus style={{ flex:1, padding:"5px 9px", borderRadius:8, border:"1.5px solid rgba(93,184,50,.4)", background:"rgba(255,255,255,.08)", color:"#f1f5f9", fontSize:13, outline:"none", fontFamily:"inherit" }}/>
                      <button onClick={()=>saveRename("hub_"+d.device_id,editName)} style={{ padding:"5px 10px", borderRadius:8, border:"none", background:"#5db832", color:"#fff", fontSize:12, fontWeight:700, cursor:"pointer" }}>✓</button>
                      <button onClick={()=>setEditingId(null)} style={{ padding:"5px 10px", borderRadius:8, border:"1px solid rgba(255,255,255,.1)", background:"transparent", color:"#475569", fontSize:12, cursor:"pointer" }}>✕</button>
                    </div>
                  ) : (
                    <>
                      <div style={{ color:"#f1f5f9", fontWeight:700, fontSize:13 }}>{displayName}</div>
                      <div style={{ display:"flex", alignItems:"center", gap:6, marginTop:2 }}>
                        <span style={{ color:"#5db832", fontSize:10, fontWeight:700, background:"rgba(93,184,50,.1)", padding:"1px 6px", borderRadius:5 }}>HA</span>
                        <span style={{ color:"#334155", fontSize:10 }}>{d.type} · <span style={{ color:on?"#5db832":"#475569" }}>{d.state}</span></span>
                      </div>
                    </>
                  )}
                </div>
                {!isEditing && (
                  <Toggle on={on} onChange={()=>{
                    const domain = d.type==="light"?"light":d.type==="lock"?"lock":"switch";
                    const service = on?"turn_off":"turn_on";
                    fetch(`${LIVATO_SERVER}/ha/service`,{method:"POST",headers:{"Content-Type":"application/json","Authorization":`Bearer ${token}`},body:JSON.stringify({domain,service,entity_id:d.device_id})});
                    setHubDevices(prev=>prev.map(x=>x.device_id===d.device_id?{...x,state:service==="turn_on"?"on":"off"}:x));
                  }}/>
                )}
              </div>
              {!isEditing && (
                <div style={{ marginTop:10 }}>
                  <button onClick={()=>{setEditingId("hub_"+d.device_id);setEditName(displayName);}} style={{ padding:"5px 12px", borderRadius:8, border:"1px solid rgba(255,255,255,.08)", background:"rgba(255,255,255,.04)", color:"#64748b", fontSize:11, cursor:"pointer" }}>✏️ Rename</button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {hidden.length > 0 && <button onClick={()=>{setHidden([]);localStorage.removeItem("livato_hidden");}} style={{ width:"100%", marginTop:12, padding:"10px", borderRadius:12, border:"1px solid rgba(255,255,255,.08)", background:"rgba(255,255,255,.03)", color:"#334155", fontSize:12, cursor:"pointer" }}>{hidden.length} hidden · tap to restore all</button>}
      {showDiscovery && <DeviceDiscovery onClose={()=>setShowDiscovery(false)}/>}
    </div>
  );
}

// ─── Scene Editor ─────────────────────────────────────────────────────────────
function SceneEditor({ scenes, setScenes }) {
  const [editing, setEditing]   = useState(null);
  const [editName, setEditName] = useState("");
  const [editIcon, setEditIcon] = useState("🌅");
  const [editColor, setEditColor] = useState("#f59e0b");
  const [showIcons, setShowIcons] = useState(false);
  const ICON_OPTIONS = ["🌅","🌙","🎬","🚪","🎵","🏠","💡","🌿","🎮","🍽️","🛁","💪","📚","🎨","❄️","☀️","🌧","🔥","🎉","😴"];
  const COLOR_OPTIONS = ["#f59e0b","#6366f1","#06b6d4","#ec4899","#5db832","#f97316","#a78bfa","#e11d48","#10b981","#14b8a6"];
  const startEdit = s => { setEditing(s.id); setEditName(s.label); setEditIcon(s.icon); setEditColor(s.color); setShowIcons(false); };
  const saveEdit = () => {
    const u = scenes.map(s=>s.id===editing?{...s,label:editName,icon:editIcon,color:editColor}:s);
    setScenes(u); localStorage.setItem("livato_scenes",JSON.stringify(u)); setEditing(null);
  };
  const deleteScene = id => { const u=scenes.filter(s=>s.id!==id); setScenes(u); localStorage.setItem("livato_scenes",JSON.stringify(u)); };
  const addScene = () => {
    const s = { id:"scene_"+Date.now(), label:"New Scene", icon:"⭐", color:"#5db832", actions:[] };
    const u = [...scenes,s]; setScenes(u); localStorage.setItem("livato_scenes",JSON.stringify(u)); startEdit(s);
  };
  return (
    <div>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
        <div style={{ fontSize:11, fontWeight:700, color:"#334155", letterSpacing:".07em", textTransform:"uppercase" }}>Scenes</div>
        <button onClick={addScene} style={{ padding:"6px 14px", borderRadius:10, border:"1.5px solid rgba(93,184,50,.3)", background:"rgba(93,184,50,.08)", color:"#5db832", fontSize:12, fontWeight:700, cursor:"pointer" }}>+ New Scene</button>
      </div>
      <div style={{ background:"rgba(255,165,0,.06)", border:"1.5px solid rgba(255,165,0,.15)", borderRadius:12, padding:"10px 14px", marginBottom:12 }}>
        <div style={{ color:"#f59e0b", fontSize:12, fontWeight:700 }}>⚙️ Scenes need HA connection to activate devices</div>
        <div style={{ color:"#64748b", fontSize:11, marginTop:2 }}>Customise name and icon here. Device actions fire via Home Assistant when tapped.</div>
      </div>
      <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
        {scenes.map(s=>(
          <div key={s.id} style={{ background:"rgba(255,255,255,.04)", border:`1.5px solid ${editing===s.id?s.color+"55":"rgba(255,255,255,.08)"}`, borderRadius:16, padding:"14px" }}>
            {editing===s.id ? (
              <div>
                <div style={{ display:"flex", gap:10, marginBottom:12 }}>
                  <button onClick={()=>setShowIcons(!showIcons)} style={{ width:48, height:48, borderRadius:12, border:`2px solid ${editColor}55`, background:editColor+"18", fontSize:24, cursor:"pointer", flexShrink:0 }}>{editIcon}</button>
                  <input value={editName} onChange={e=>setEditName(e.target.value)} style={{ flex:1, padding:"10px 12px", borderRadius:10, border:"1.5px solid rgba(255,255,255,.15)", background:"rgba(255,255,255,.08)", color:"#f1f5f9", fontSize:14, outline:"none", fontFamily:"inherit" }}/>
                </div>
                {showIcons && <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:12, padding:"10px", background:"rgba(0,0,0,.3)", borderRadius:12 }}>{ICON_OPTIONS.map(ico=><button key={ico} onClick={()=>{setEditIcon(ico);setShowIcons(false);}} style={{ width:36,height:36,borderRadius:8,border:`2px solid ${ico===editIcon?"rgba(93,184,50,.6)":"rgba(255,255,255,.06)"}`,background:ico===editIcon?"rgba(93,184,50,.15)":"rgba(255,255,255,.04)",fontSize:18,cursor:"pointer" }}>{ico}</button>)}</div>}
                <div style={{ display:"flex", gap:6, marginBottom:12 }}>{COLOR_OPTIONS.map(c=><button key={c} onClick={()=>setEditColor(c)} style={{ width:28,height:28,borderRadius:"50%",background:c,border:`2px solid ${c===editColor?"#fff":"transparent"}`,cursor:"pointer",boxShadow:c===editColor?`0 0 8px ${c}`:"none" }}/>)}</div>
                <div style={{ display:"flex", gap:8 }}>
                  <button onClick={saveEdit} style={{ flex:2,padding:"10px",borderRadius:10,border:"none",background:"#5db832",color:"#fff",fontWeight:700,fontSize:13,cursor:"pointer" }}>Save</button>
                  <button onClick={()=>setEditing(null)} style={{ flex:1,padding:"10px",borderRadius:10,border:"1px solid rgba(255,255,255,.1)",background:"transparent",color:"#475569",fontSize:13,cursor:"pointer" }}>Cancel</button>
                </div>
              </div>
            ) : (
              <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                <div style={{ width:44,height:44,borderRadius:12,background:s.color+"18",border:`1.5px solid ${s.color}44`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,flexShrink:0 }}>{s.icon}</div>
                <div style={{ flex:1 }}><div style={{ color:"#f1f5f9",fontWeight:700,fontSize:14 }}>{s.label}</div><div style={{ color:"#334155",fontSize:11,marginTop:1 }}>⚙️ Tap Edit to configure</div></div>
                <button onClick={()=>startEdit(s)} style={{ padding:"6px 12px",borderRadius:8,border:"1px solid rgba(255,255,255,.1)",background:"rgba(255,255,255,.04)",color:"#64748b",fontSize:12,cursor:"pointer" }}>Edit</button>
                <button onClick={()=>deleteScene(s.id)} style={{ width:30,height:30,borderRadius:"50%",border:"1px solid rgba(239,68,68,.2)",background:"rgba(239,68,68,.06)",color:"#f87171",fontSize:14,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center" }}>×</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Savings Tab ──────────────────────────────────────────────────────────────
const SAVINGS_RATES = { sprinkler_skip:0.50, light_auto_off:0.02, thermostat:0.30, lock_auto:0.10, scene:0.05 };
const SAVINGS_META  = {
  sprinkler_skip:{ label:"Sprinkler skip",   icon:"💧", color:"#06b6d4" },
  light_auto_off:{ label:"Light auto-off",   icon:"💡", color:"#f59e0b" },
  thermostat:    { label:"Thermostat save",  icon:"🌡️", color:"#ec4899" },
  lock_auto:     { label:"Auto lock",        icon:"🔒", color:"#6366f1" },
  scene:         { label:"Scene activated",  icon:"🎬", color:"#5db832" },
};
function SavingsTab({ userId }) {
  const [events, setEvents]   = useState([]);
  const [loading, setLoading] = useState(true);
  const SUB = 9.00;
  useEffect(() => {
    if (!userId) { setLoading(false); return; }
    const token = localStorage.getItem("livato_token");
    fetch(`${LIVATO_SERVER}/savings/summary`, { headers:{ "Authorization":`Bearer ${token}` } })
      .then(r=>r.json()).then(d=>{ setEvents(d.events||[]); setLoading(false); })
      .catch(()=>setLoading(false));
  }, [userId]);
  const now = new Date();
  const monthEvents = events.filter(e=>{ const d=new Date(e.created_at); return d.getMonth()===now.getMonth()&&d.getFullYear()===now.getFullYear(); });
  const monthSaved  = monthEvents.reduce((s,e)=>s+parseFloat(e.value_usd||0),0);
  const totalSaved  = events.reduce((s,e)=>s+parseFloat(e.value_usd||0),0);
  const pct         = Math.min((monthSaved/SUB)*100,100);
  const paidOff     = monthSaved >= SUB;
  const daily       = monthSaved / Math.max(now.getDate(),1);
  const daysLeft    = paidOff ? 0 : Math.ceil((SUB-monthSaved)/Math.max(daily,0.01));
  const byCategory  = Object.entries(SAVINGS_META).map(([key,meta])=>({ ...meta, key, total:events.filter(e=>e.type===key).reduce((s,e)=>s+parseFloat(e.value_usd||0),0), count:events.filter(e=>e.type===key).length })).filter(c=>c.count>0);
  if (loading) return <div style={{ textAlign:"center",padding:"60px 20px",color:"#475569" }}><div style={{ fontSize:32,marginBottom:12 }}>💰</div><div>Loading your savings…</div></div>;
  return (
    <div style={{ fontFamily:"sans-serif" }}>
      {/* Hero */}
      <div style={{ background:"linear-gradient(135deg,rgba(93,184,50,.12),rgba(34,197,94,.06))", border:"1.5px solid rgba(93,184,50,.25)", borderRadius:24, padding:"24px 20px", marginBottom:14, textAlign:"center" }}>
        <div style={{ color:"#5db832",fontSize:11,fontWeight:700,letterSpacing:".1em",textTransform:"uppercase",marginBottom:8 }}>This month</div>
        <div style={{ color:"#f1f5f9",fontSize:52,fontWeight:800,letterSpacing:"-2px",marginBottom:4 }}>${monthSaved.toFixed(2)}</div>
        <div style={{ color:"#64748b",fontSize:13,marginBottom:20 }}>saved by your automations</div>
        <div style={{ background:"rgba(255,255,255,.08)",borderRadius:8,height:8,overflow:"hidden",marginBottom:8 }}>
          <div style={{ height:"100%",width:`${pct}%`,background:paidOff?"#22c55e":"#5db832",borderRadius:8,transition:"width .5s" }}/>
        </div>
        <div style={{ display:"flex",justifyContent:"space-between",fontSize:11,color:"#475569" }}><span>${monthSaved.toFixed(2)} saved</span><span>${SUB.toFixed(2)}/mo subscription</span></div>
        {paidOff
          ? <div style={{ marginTop:14,padding:"10px 16px",borderRadius:12,background:"rgba(34,197,94,.15)",border:"1px solid rgba(34,197,94,.3)",color:"#4ade80",fontWeight:700,fontSize:14 }}>✓ Livato has paid for itself this month!</div>
          : <div style={{ marginTop:14,color:"#64748b",fontSize:12 }}>${(SUB-monthSaved).toFixed(2)} more to break even · est. {daysLeft} days</div>
        }
      </div>
      {/* Stats */}
      <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:14 }}>
        <div style={{ background:"rgba(255,255,255,.04)",border:"1.5px solid rgba(255,255,255,.07)",borderRadius:16,padding:"14px" }}>
          <div style={{ color:"#334155",fontSize:11,fontWeight:700,marginBottom:4 }}>All time</div>
          <div style={{ color:"#f1f5f9",fontWeight:800,fontSize:22 }}>${totalSaved.toFixed(2)}</div>
          <div style={{ color:"#475569",fontSize:11,marginTop:2 }}>{events.length} events</div>
        </div>
        <div style={{ background:"rgba(255,255,255,.04)",border:"1.5px solid rgba(255,255,255,.07)",borderRadius:16,padding:"14px" }}>
          <div style={{ color:"#334155",fontSize:11,fontWeight:700,marginBottom:4 }}>Daily average</div>
          <div style={{ color:"#f1f5f9",fontWeight:800,fontSize:22 }}>${daily.toFixed(2)}</div>
          <div style={{ color:"#475569",fontSize:11,marginTop:2 }}>per day this month</div>
        </div>
      </div>
      {/* By category */}
      {byCategory.length > 0 && (
        <div style={{ marginBottom:14 }}>
          <div style={{ fontSize:11,fontWeight:700,color:"#334155",letterSpacing:".07em",textTransform:"uppercase",marginBottom:10 }}>By Category</div>
          {byCategory.sort((a,b)=>b.total-a.total).map(c=>(
            <div key={c.key} style={{ background:"rgba(255,255,255,.04)",border:"1.5px solid rgba(255,255,255,.07)",borderRadius:14,padding:"12px 14px",display:"flex",alignItems:"center",gap:12,marginBottom:6 }}>
              <div style={{ width:36,height:36,borderRadius:10,background:c.color+"18",border:`1.5px solid ${c.color}33`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0 }}>{c.icon}</div>
              <div style={{ flex:1 }}><div style={{ color:"#f1f5f9",fontWeight:700,fontSize:13 }}>{c.label}</div><div style={{ color:"#475569",fontSize:11 }}>{c.count} events</div></div>
              <div style={{ color:c.color,fontWeight:800,fontSize:16 }}>${c.total.toFixed(2)}</div>
            </div>
          ))}
        </div>
      )}
      {/* Empty state */}
      {events.length === 0 && (
        <div style={{ textAlign:"center",padding:"40px 20px",border:"1.5px dashed rgba(255,255,255,.1)",borderRadius:16 }}>
          <div style={{ fontSize:40,marginBottom:12 }}>📊</div>
          <div style={{ color:"#475569",fontWeight:700,fontSize:14,marginBottom:6 }}>No savings recorded yet</div>
          <div style={{ color:"#334155",fontSize:12,lineHeight:1.6 }}>Savings are tracked automatically when your automations fire. Enable automations to start seeing your impact.</div>
        </div>
      )}
      {/* Recent */}
      {events.length > 0 && (
        <div>
          <div style={{ fontSize:11,fontWeight:700,color:"#334155",letterSpacing:".07em",textTransform:"uppercase",marginBottom:10 }}>Recent Activity</div>
          {events.slice(0,10).map((e,i)=>(
            <div key={i} style={{ display:"flex",alignItems:"center",gap:10,padding:"10px 14px",background:"rgba(255,255,255,.03)",border:"1px solid rgba(255,255,255,.06)",borderRadius:12,marginBottom:6 }}>
              <span style={{ fontSize:16 }}>{SAVINGS_META[e.type]?.icon||"⚡"}</span>
              <div style={{ flex:1 }}><div style={{ color:"#94a3b8",fontSize:12 }}>{e.description||SAVINGS_META[e.type]?.label||e.type}</div><div style={{ color:"#334155",fontSize:10,marginTop:1 }}>{new Date(e.created_at).toLocaleDateString()}</div></div>
              <div style={{ color:"#5db832",fontWeight:700,fontSize:13 }}>+${parseFloat(e.value_usd).toFixed(2)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Deep Theming Tab ─────────────────────────────────────────────────────────
function DeepThemingTab() {
  const g = k => localStorage.getItem("livato_"+k) || {theme:"green",card_style:"default",radius:"16",font:"serif",density:"comfortable",bg_style:"solid"}[k];
  const [colorId,   setColorId]   = useState(g("theme"));
  const [cardStyle, setCardStyle] = useState(g("card_style"));
  const [radius,    setRadius]    = useState(g("radius"));
  const [fontStyle, setFontStyle] = useState(g("font"));
  const [density,   setDensity]   = useState(g("density"));
  const [bgStyle,   setBgStyle]   = useState(g("bg_style"));
  const apply = () => {
    ["theme","card_style","radius","font","density","bg_style"].forEach((k,i)=>{
      localStorage.setItem("livato_"+k,[colorId,cardStyle,radius,fontStyle,density,bgStyle][i]);
    });
    window.location.reload();
  };
  const Section = ({label,children}) => <div style={{ marginBottom:20 }}><div style={{ fontSize:11,fontWeight:700,color:"#334155",letterSpacing:".07em",textTransform:"uppercase",marginBottom:10 }}>{label}</div>{children}</div>;
  const OptionRow = ({options,value,onChange}) => (
    <div style={{ display:"flex",gap:6 }}>
      {options.map(o=><button key={o.value} onClick={()=>onChange(o.value)} style={{ flex:1,padding:"10px 6px",borderRadius:12,border:`1.5px solid ${value===o.value?"rgba(93,184,50,.4)":"rgba(255,255,255,.08)"}`,background:value===o.value?"rgba(93,184,50,.1)":"rgba(255,255,255,.03)",color:value===o.value?"#5db832":"#475569",fontSize:11,fontWeight:700,cursor:"pointer",textAlign:"center" }}>{o.label}</button>)}
    </div>
  );
  return (
    <div style={{ fontFamily:"sans-serif" }}>
      <div style={{ marginBottom:20 }}><div style={{ fontSize:18,fontFamily:"Georgia,serif",fontWeight:400,color:"#f1f5f9" }}>Themes</div><div style={{ fontSize:13,color:"#475569",marginTop:2 }}>Customize the look and feel of Livato</div></div>
      <Section label="Color scheme">
        <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:8 }}>
          {THEMES.map(t=>(
            <button key={t.id} onClick={()=>setColorId(t.id)} style={{ padding:"14px 12px",borderRadius:16,border:`2px solid ${colorId===t.id?t.primary:"rgba(255,255,255,.08)"}`,background:colorId===t.id?t.primary+"15":"rgba(255,255,255,.03)",cursor:"pointer",display:"flex",alignItems:"center",gap:10 }}>
              <div style={{ width:28,height:28,borderRadius:"50%",background:t.primary,boxShadow:colorId===t.id?`0 0 12px ${t.primary}88`:"none",flexShrink:0 }}/>
              <div style={{ textAlign:"left" }}><div style={{ color:colorId===t.id?t.primary:"#94a3b8",fontWeight:700,fontSize:13 }}>{t.name}</div>{colorId===t.id&&<div style={{ fontSize:10,color:t.primary }}>✓ Active</div>}</div>
            </button>
          ))}
        </div>
      </Section>
      <Section label="Card style"><OptionRow options={[{value:"default",label:"Default"},{value:"glass",label:"Glass"},{value:"outlined",label:"Outlined"},{value:"flat",label:"Flat"}]} value={cardStyle} onChange={setCardStyle}/></Section>
      <Section label="Corner radius">
        <div style={{ display:"flex",alignItems:"center",gap:12,marginBottom:10 }}>
          <span style={{ color:"#475569",fontSize:12 }}>Sharp</span>
          <input type="range" min={0} max={28} value={radius} onChange={e=>setRadius(e.target.value)} style={{ flex:1,accentColor:"#5db832" }}/>
          <span style={{ color:"#475569",fontSize:12 }}>Pill</span>
          <span style={{ background:"rgba(93,184,50,.12)",border:"1px solid rgba(93,184,50,.25)",borderRadius:7,padding:"2px 9px",color:"#5db832",fontWeight:700,fontSize:12 }}>{radius}px</span>
        </div>
        <div style={{ display:"flex",gap:8 }}>{[0,8,16,24].map(r=><div key={r} style={{ flex:1,height:36,borderRadius:r,border:`2px solid ${parseInt(radius)===r?"#5db832":"rgba(255,255,255,.1)"}`,background:parseInt(radius)===r?"rgba(93,184,50,.1)":"rgba(255,255,255,.03)",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center" }} onClick={()=>setRadius(String(r))}><span style={{ color:parseInt(radius)===r?"#5db832":"#334155",fontSize:10,fontWeight:700 }}>{r===0?"Sharp":r===24?"Pill":r+"px"}</span></div>)}</div>
      </Section>
      <Section label="Font"><OptionRow options={[{value:"serif",label:"Serif"},{value:"sans",label:"Sans"},{value:"mono",label:"Mono"},{value:"rounded",label:"Rounded"}]} value={fontStyle} onChange={setFontStyle}/></Section>
      <Section label="Layout density"><OptionRow options={[{value:"compact",label:"Compact"},{value:"comfortable",label:"Comfortable"},{value:"spacious",label:"Spacious"}]} value={density} onChange={setDensity}/></Section>
      <Section label="Background"><OptionRow options={[{value:"solid",label:"Solid"},{value:"gradient",label:"Gradient"},{value:"mesh",label:"Mesh"}]} value={bgStyle} onChange={setBgStyle}/></Section>
      <button onClick={apply} style={{ width:"100%",padding:"16px",borderRadius:16,border:"none",background:"linear-gradient(135deg,#5db832,#3d7a1a)",color:"#fff",fontWeight:700,fontSize:15,cursor:"pointer",boxShadow:"0 4px 20px rgba(93,184,50,.4)",marginTop:8 }}>Apply Theme</button>
      <div style={{ marginTop:12,padding:"12px 14px",borderRadius:12,background:"rgba(255,255,255,.03)",border:"1px solid rgba(255,255,255,.07)" }}>
        <div style={{ color:"#334155",fontSize:12 }}>⚙️ Lucide icon library and animation settings coming in the next update.</div>
      </div>
    </div>
  );
}

// ─── More Tab (sub-navigation) ────────────────────────────────────────────────
function MoreTab({ scenes, setScenes, weather, toTemp, onAddSuggestion, existingIds }) {
  const [subTab, setSubTab] = useState("scenes");
  const SUB = [{ key:"scenes",label:"Scenes",icon:"🎬" },{ key:"weather",label:"Weather",icon:"🌤" },{ key:"rooms",label:"Rooms",icon:"▣" },{ key:"themes",label:"Themes",icon:"🎨" }];
  return (
    <div style={{ fontFamily:"sans-serif" }}>
      <div style={{ display:"flex",gap:6,marginBottom:16,overflowX:"auto",paddingBottom:2 }}>
        {SUB.map(s=><button key={s.key} onClick={()=>setSubTab(s.key)} style={{ padding:"7px 14px",borderRadius:20,border:`1.5px solid ${subTab===s.key?"rgba(93,184,50,.4)":"rgba(255,255,255,.07)"}`,background:subTab===s.key?"rgba(93,184,50,.1)":"rgba(255,255,255,.03)",color:subTab===s.key?"#5db832":"#475569",fontSize:12,fontWeight:700,cursor:"pointer",flexShrink:0 }}>{s.icon} {s.label}</button>)}
      </div>
      {subTab==="scenes"  && <SceneEditor scenes={scenes} setScenes={setScenes}/>}
      {subTab==="weather" && <WeatherTab onAddSuggestion={onAddSuggestion} existingIds={existingIds} weather={weather} toTemp={toTemp}/>}
      {subTab==="rooms"   && <FloorPlan/>}
      {subTab==="themes"  && <DeepThemingTab/>}
    </div>
  );
}

const TABS = [
  { key:"home",        label:"Home",    icon:"🏠" },
  { key:"devices",     label:"Devices", icon:"💡" },
  { key:"automations", label:"Automate",icon:"⚡" },
  { key:"savings",     label:"Savings", icon:"💰" },
  { key:"music",       label:"Music",   icon:"🎵" },
  { key:"more",        label:"More",    icon:"☰" },
];

const THEMES = [
  { id:"green",   name:"Forest",   primary:"#5db832", bg:"#070e04", bg2:"#0c1a08" },
  { id:"blue",    name:"Ocean",    primary:"#0ea5e9", bg:"#030b12", bg2:"#071525" },
  { id:"purple",  name:"Galaxy",   primary:"#a78bfa", bg:"#07030e", bg2:"#120825" },
  { id:"orange",  name:"Sunset",   primary:"#f97316", bg:"#0e0702", bg2:"#1a0e04" },
  { id:"pink",    name:"Rose",     primary:"#ec4899", bg:"#0e0308", bg2:"#1a0510" },
  { id:"cyan",    name:"Arctic",   primary:"#06b6d4", bg:"#020c0e", bg2:"#041419" },
  { id:"gold",    name:"Amber",    primary:"#f59e0b", bg:"#0e0b02", bg2:"#1a1504" },
  { id:"white",   name:"Minimal",  primary:"#e2e8f0", bg:"#050505", bg2:"#0f0f0f" },
];

// ─── Auth Screen ──────────────────────────────────────────────────────
function AuthScreen({ onLogin }) {
  const [mode, setMode]       = useState('login');
  const [name, setName]       = useState('');
  const [email, setEmail]     = useState('');
  const [password, setPass]   = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const SERVER = 'https://livato-server-production.up.railway.app';

  const submit = async () => {
    setError('');
    if (!email || !password) return setError('Please enter email and password');
    if (mode === 'register' && !name) return setError('Please enter your name');
    setLoading(true);
    try {
      const res = await fetch(`${SERVER}/auth/${mode}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, name })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Something went wrong');
      localStorage.setItem('livato_token', data.token);
      localStorage.setItem('livato_user', JSON.stringify(data.user));
      onLogin(data.user);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  return (
    <div style={{ minHeight:'100vh', background:'#070e04', display:'flex', alignItems:'center', justifyContent:'center', padding:'20px', fontFamily:'Outfit,sans-serif' }}>
      <div style={{ width:'100%', maxWidth:400 }}>
        <div style={{ textAlign:'center', marginBottom:40 }}>
          <div style={{ fontFamily:'Georgia,serif', fontSize:42, color:'#5db832', letterSpacing:'-1px', marginBottom:8 }}>Livato</div>
          <div style={{ color:'#475569', fontSize:14 }}>Your life, automated</div>
        </div>
        <div style={{ background:'rgba(255,255,255,.04)', border:'1.5px solid rgba(255,255,255,.08)', borderRadius:24, padding:32 }}>
          <div style={{ display:'flex', gap:4, background:'rgba(255,255,255,.04)', borderRadius:12, padding:4, marginBottom:28 }}>
            {['login','register'].map(m => (
              <button key={m} onClick={() => { setMode(m); setError(''); }}
                style={{ flex:1, padding:'10px', borderRadius:9, border:'none', cursor:'pointer', fontSize:13, fontWeight:700, fontFamily:'inherit',
                  background: mode===m ? '#5db832' : 'transparent',
                  color: mode===m ? '#fff' : '#475569' }}>
                {m === 'login' ? 'Sign In' : 'Create Account'}
              </button>
            ))}
          </div>
          {mode === 'register' && (
            <div style={{ marginBottom:16 }}>
              <div style={{ color:'#64748b', fontSize:12, fontWeight:600, marginBottom:6 }}>Your Name</div>
              <input value={name} onChange={e => setName(e.target.value)}
                placeholder="Steve" type="text"
                style={{ width:'100%', padding:'12px 14px', borderRadius:12, border:'1.5px solid rgba(255,255,255,.1)', background:'rgba(255,255,255,.06)', color:'#f1f5f9', fontSize:14, outline:'none', fontFamily:'inherit', boxSizing:'border-box' }} />
            </div>
          )}
          <div style={{ marginBottom:16 }}>
            <div style={{ color:'#64748b', fontSize:12, fontWeight:600, marginBottom:6 }}>Email</div>
            <input value={email} onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com" type="email"
              onKeyDown={e => e.key==='Enter' && submit()}
              style={{ width:'100%', padding:'12px 14px', borderRadius:12, border:'1.5px solid rgba(255,255,255,.1)', background:'rgba(255,255,255,.06)', color:'#f1f5f9', fontSize:14, outline:'none', fontFamily:'inherit', boxSizing:'border-box' }} />
          </div>
          <div style={{ marginBottom:24 }}>
            <div style={{ color:'#64748b', fontSize:12, fontWeight:600, marginBottom:6 }}>Password</div>
            <input value={password} onChange={e => setPass(e.target.value)}
              placeholder="••••••••" type="password"
              onKeyDown={e => e.key==='Enter' && submit()}
              style={{ width:'100%', padding:'12px 14px', borderRadius:12, border:'1.5px solid rgba(255,255,255,.1)', background:'rgba(255,255,255,.06)', color:'#f1f5f9', fontSize:14, outline:'none', fontFamily:'inherit', boxSizing:'border-box' }} />
          </div>
          {error && (
            <div style={{ background:'rgba(239,68,68,.1)', border:'1px solid rgba(239,68,68,.3)', borderRadius:10, padding:'10px 14px', color:'#fca5a5', fontSize:13, marginBottom:16 }}>
              {error}
            </div>
          )}
          <button onClick={submit} disabled={loading}
            style={{ width:'100%', padding:'14px', borderRadius:12, border:'none', background:loading?'#334155':'#5db832', color:'#fff', fontSize:15, fontWeight:700, cursor:loading?'not-allowed':'pointer', fontFamily:'inherit', boxShadow:'0 4px 20px rgba(93,184,50,.4)' }}>
            {loading ? 'Please wait…' : mode === 'login' ? 'Sign In' : 'Create Account'}
          </button>
          {mode === 'login' && (
            <div style={{ textAlign:'center', marginTop:16, color:'#475569', fontSize:13 }}>
              No account? <span onClick={() => setMode('register')} style={{ color:'#5db832', cursor:'pointer', fontWeight:600 }}>Create one free</span>
            </div>
          )}
        </div>
        <div style={{ textAlign:'center', marginTop:20, color:'#1e293b', fontSize:12 }}>
          By continuing you agree to our <a href="https://getlivato.com/terms.html" style={{ color:'#334155' }}>Terms</a> and <a href="https://getlivato.com/privacy.html" style={{ color:'#334155' }}>Privacy Policy</a>
        </div>
      </div>
    </div>
  );
}export default function App() {
const [user, setUser] = useState(() => {
    const saved = localStorage.getItem('livato_user');
    return saved ? JSON.parse(saved) : null;
  });

  const [tab, setTab] = useState("home");
  useEffect(() => {
    const handler = (e) => setTab(e.detail);
    window.addEventListener("livato_goto_tab", handler);
    return () => window.removeEventListener("livato_goto_tab", handler);
  }, []);
  // Track YouTube now-playing at App level — start null so nothing auto-plays on load
const [ytTrackId, setYtTrackId] = useState(null);

useEffect(() => {
  const handler = () => {
    try {
      const np = localStorage.getItem("livato_now_playing");
      setYtTrackId(np ? JSON.parse(np).id : null);
    } catch { setYtTrackId(null); }
  };
  window.addEventListener("livato_music_change", handler);
  return () => window.removeEventListener("livato_music_change", handler);
}, []);

    const [showProfile, setShowProfile] = useState(false);
  const [weatherData, setWeatherData] = useState(null);
  const [tempUnitGlobal, setTempUnitGlobal] = useState(
    localStorage.getItem("livato_temp_unit") || (navigator.language === "en-US" ? "F" : "C")
  );
  const toTempGlobal = (c) => tempUnitGlobal === "F" 
    ? `${Math.round(c * 9/5 + 32)}°F` 
    : `${Math.round(c)}°C`;

  // ── HA Devices — lifted to App level so Devices tab + Home screen share the same state
  const [haDevices, setHaDevices] = useState([]);
  const loadHADevices = () => {
    fetch(`${LIVATO_SERVER}/ha/states`)
      .then(r=>r.json())
      .then(data=>{ if(Array.isArray(data)) setHaDevices(data.filter(d=>["switch.","light.","climate.","lock.","media_player."].some(p=>d.entity_id.startsWith(p)))); })
      .catch(()=>{
        fetch(`${HA_URL}/api/states`,{headers:{"Authorization":`Bearer ${HA_TOKEN}`}})
          .then(r=>r.json()).then(data=>{ if(Array.isArray(data)) setHaDevices(data.filter(d=>d.entity_id.startsWith("switch.")||d.entity_id.startsWith("light."))); })
          .catch(()=>{});
      });
  };
  useEffect(()=>{ loadHADevices(); },[]);
  const toggleHADevice = (entityId, currentState) => {
    const domain = entityId.startsWith("light.")?"light":"switch";
    const service = currentState==="on"?"turn_off":"turn_on";
    fetch(`${LIVATO_SERVER}/ha/service`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({domain,service,entity_id:entityId})}).catch(()=>{});
    setHaDevices(prev=>prev.map(d=>d.entity_id===entityId?{...d,state:service==="turn_on"?"on":"off"}:d));
  };

  // ── Scenes state
  const [scenes, setScenes] = useState(() => {
    try { const s=localStorage.getItem("livato_scenes"); return s?JSON.parse(s):QUICK_SCENES; } catch { return QUICK_SCENES; }
  });

  const [automations,setAutos] = useState(() => {
    try {
      const saved = localStorage.getItem("livato_automations");
      return saved ? JSON.parse(saved) : INIT_AUTOMATIONS;
    } catch { return INIT_AUTOMATIONS; }
  });
  const [building,   setBuilding] = useState(false);
  const [step,       setStep]  = useState(1);
  const [saved,      setSaved] = useState(false);

  const [bTD,   setBTD]   = useState(null);
  const [bTE,   setBTE]   = useState(null);
  const [bUT,   setBUT]   = useState(false);
  const [bTF,   setBTF]   = useState("07:00");
  const [bTT,   setBTT]   = useState("21:00");
  const [bDays, setBDays] = useState([0,1,2,3,4,5,6]);
  const [bActs, setBActs] = useState([{ type:"device", deviceId:null, action:null }]);
  const [bName, setBName] = useState("");

  const triggerDev  = DEVICES_INIT.find(d=>d.id===bTD);
  const triggerCaps = triggerDev?.capabilities.triggers||[];
  const actsValid   = bActs.length>0 && bActs.every(a=>a.type==="notify"?a.message.trim().length>0:(a.deviceId&&a.action));

  const resetBuilder = () => { setBuilding(false);setStep(1);setBTD(null);setBTE(null);setBUT(false);setBTF("07:00");setBTT("21:00");setBDays([0,1,2,3,4,5,6]);setBActs([{type:"device",deviceId:null,action:null}]);setBName("");setSaved(false); };

  const saveAuto = () => {
    const newAuto = { id:Date.now(), name:bName||`${triggerDev?.name} automation`, enabled:true, lastTriggered:"Never",
      trigger:{deviceId:bTD,event:bTE}, condition:{useTime:bUT,timeFrom:bTF,timeTo:bTT,days:bDays}, actions:bActs };
    setAutos(p => {
      const updated = [newAuto, ...p];
      localStorage.setItem("livato_automations", JSON.stringify(updated));
      return updated;
    });
    setSaved(true);
    setTimeout(()=>{ resetBuilder(); setTab("automations"); }, 1400);
  };
if (!user) return <AuthScreen onLogin={(u) => setUser(u)} />;
  const addSuggestion = s => setAutos(p=>[{ id:s.id+"_weather", name:s.title, enabled:true, lastTriggered:"Never", trigger:s.trigger, condition:s.condition, actions:s.actions },...p]);
  const existingIds = automations.map(a=>String(a.id));

  // Log a savings event to the server
  const logSaving = async (type, description, value_usd) => {
    const token = localStorage.getItem("livato_token");
    if (!token) return;
    try {
      await fetch(`${LIVATO_SERVER}/savings/event`, {
        method:"POST",
        headers:{ "Content-Type":"application/json", "Authorization":`Bearer ${token}` },
        body: JSON.stringify({ type, description, value_usd })
      });
    } catch {}
  };

  return (
    <div style={{ minHeight:"100vh",background: (() => { const t=THEMES.find(x=>x.id===(localStorage.getItem("livato_theme")||"green")); return t?.bg||"#070e04"; })(),fontFamily:"Georgia,serif",color:"#f1f5f9" }}>
      {/* Ambient background */}
      <div style={{ position:"fixed",inset:0,pointerEvents:"none",zIndex:0,background:"radial-gradient(ellipse 80% 50% at 50% 0%,rgba(93,184,50,.07) 0%,transparent 60%),radial-gradient(ellipse 60% 40% at 85% 90%,rgba(93,184,50,.05) 0%,transparent 60%)" }}/>

      <div style={{ position:"relative",zIndex:1,width:"100%",paddingBottom:90 }}>

        {/* Header */}
        <div style={{ padding:"22px 20px 0",display:"flex",alignItems:"center",justifyContent:"space-between",maxWidth:960,margin:"0 auto" }}>
          <div style={{ display:"flex",alignItems:"center",gap:12 }}>
            <button style={{ width:36,height:36,borderRadius:"50%",background:"rgba(255,255,255,.06)",border:"1.5px solid rgba(255,255,255,.08)",cursor:"pointer",fontSize:14,position:"relative",flexShrink:0 }}>
              🔔<div style={{ position:"absolute",top:2,right:2,width:12,height:12,borderRadius:"50%",background:"#5db832",fontSize:7,fontWeight:700,color:"#000",display:"flex",alignItems:"center",justifyContent:"center" }}>2</div>
            </button>
            <div>
              <div style={{ fontFamily:"Georgia,serif",fontSize:20,fontWeight:400,letterSpacing:"-.3px",color:"#f8fafc" }}>Li<span style={{ color:"#5db832",fontStyle:"italic" }}>v</span>ato</div>
              <div style={{ color:"#334155",fontSize:11,marginTop:1,fontFamily:"sans-serif" }}>{haDevices.length > 0 ? `${haDevices.filter(d=>d.state==="on").length}/${haDevices.length} devices on` : "No devices connected"} · {automations.filter(a=>a.enabled).length} rules active</div>
            </div>
          </div>
          <div style={{ display:"flex",alignItems:"center",gap:8 }}>
            <div style={{ position:"relative" }}>
              <button onClick={() => setShowProfile(p=>!p)} style={{ width:38,height:38,borderRadius:"50%",background:"linear-gradient(135deg,#5db832,#3d7a1a)",border:"2px solid rgba(93,184,50,.4)",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,color:"#fff",fontWeight:700,flexShrink:0 }}>
                {user?.name?.[0]?.toUpperCase() || "👤"}
              </button>
              {showProfile && (
                <div style={{ position:"absolute",top:46,right:0,background:"#0c1a08",border:"1.5px solid rgba(255,255,255,.1)",borderRadius:16,padding:16,minWidth:220,zIndex:200,boxShadow:"0 8px 32px rgba(0,0,0,.5)" }}>
                  <div style={{ color:"#f1f5f9",fontWeight:700,fontSize:14,marginBottom:2 }}>{user?.name}</div>
                  <div style={{ color:"#475569",fontSize:12,marginBottom:12 }}>{user?.email}</div>
                  <div style={{ borderTop:"1px solid rgba(255,255,255,.08)",paddingTop:12,display:"flex",flexDirection:"column",gap:8 }}>
                    <div style={{ marginBottom:8 }}>
                      <div style={{ color:"#64748b",fontSize:11,fontWeight:700,marginBottom:6 }}>Home Assistant</div>
                      <input defaultValue={localStorage.getItem("livato_ha_url")||""} placeholder="http://your-ha-ip:8123" onBlur={e=>localStorage.setItem("livato_ha_url",e.target.value)} style={{ width:"100%",padding:"8px 10px",borderRadius:8,border:"1px solid rgba(255,255,255,.1)",background:"rgba(255,255,255,.05)",color:"#f1f5f9",fontSize:12,fontFamily:"inherit",outline:"none",boxSizing:"border-box",marginBottom:6 }}/>
                      <input defaultValue={localStorage.getItem("livato_ha_token")||""} placeholder="Long-lived access token" type="password" onBlur={e=>localStorage.setItem("livato_ha_token",e.target.value)} style={{ width:"100%",padding:"8px 10px",borderRadius:8,border:"1px solid rgba(255,255,255,.1)",background:"rgba(255,255,255,.05)",color:"#f1f5f9",fontSize:12,fontFamily:"inherit",outline:"none",boxSizing:"border-box" }}/>
                    </div>
                    <button onClick={()=>setTab("more")} style={{ background:"rgba(255,255,255,.05)",border:"1px solid rgba(255,255,255,.08)",borderRadius:8,padding:"8px 12px",color:"#94a3b8",fontSize:13,cursor:"pointer",textAlign:"left" }}>🎨 Themes &amp; Settings</button>
                    <button onClick={()=>{ localStorage.removeItem("livato_token"); localStorage.removeItem("livato_user"); window.location.reload(); }} style={{ background:"rgba(239,68,68,.08)",border:"1px solid rgba(239,68,68,.2)",borderRadius:8,padding:"8px 12px",color:"#f87171",fontSize:13,cursor:"pointer",textAlign:"left",fontWeight:600 }}>Sign Out</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Rain alert */}
        {weatherData?.forecast?.some(f => f.rain > 60) && (
          <div style={{ margin:"10px 20px 0",padding:"8px 12px",background:"rgba(93,184,50,.07)",border:"1px solid rgba(93,184,50,.15)",borderRadius:10,display:"flex",alignItems:"center",gap:8,maxWidth:960,marginLeft:"auto",marginRight:"auto" }}>
            <div style={{ width:6,height:6,borderRadius:"50%",background:"#5db832",boxShadow:"0 0 6px #5db832",flexShrink:0 }}/>
            <span style={{ color:"#5db832",fontSize:11,fontWeight:600,fontFamily:"sans-serif" }}>Rain expected {weatherData.forecast.find(f=>f.rain>60)?.day} — sprinklers will skip automatically 💧</span>
          </div>
        )}

        <div style={{ padding:"14px 20px 0" }}>
          {tab==="home" && <HomeScreen automations={automations} onWeatherUpdate={setWeatherData} haDevices={haDevices} setHaDevices={setHaDevices} scenes={scenes} />}
          {tab==="devices" && <DevicesTab haDevices={haDevices} onToggle={toggleHADevice} onRefresh={loadHADevices}/>}

          <div style={{ display: tab==="music" ? "block" : "none" }}>
            <div style={{ fontSize:18,fontFamily:"Georgia,serif",fontWeight:400,marginBottom:3 }}>Music</div>
            <div style={{ color:"#475569",fontSize:13,fontFamily:"sans-serif",marginBottom:16 }}>Control what's playing throughout your home</div>
            <MusicPlayer />
          </div>

          {tab==="savings" && <SavingsTab userId={user?.id}/>}

          {tab==="automations" && !building && (
            <div style={{ fontFamily:"sans-serif" }}>
              <button onClick={()=>{ setBuilding(true); setStep(1); }} style={{ width:"100%",padding:14,borderRadius:18,border:"1.5px dashed rgba(93,184,50,.4)",background:"rgba(93,184,50,.05)",color:"#5db832",fontWeight:700,fontSize:15,cursor:"pointer",marginBottom:12,display:"flex",alignItems:"center",justifyContent:"center",gap:10 }}>
                <span style={{ fontSize:20 }}>+</span>New Automation
              </button>
              <div style={{ display:"flex",flexDirection:"column",gap:8 }}>
                {automations.map(a=><AutoCard key={a.id} auto={a} onToggle={id=>setAutos(p=>{ const u=p.map(x=>x.id===id?{...x,enabled:!x.enabled}:x); localStorage.setItem("livato_automations",JSON.stringify(u)); return u; })} onDelete={id=>setAutos(p=>{ const u=p.filter(x=>x.id!==id); localStorage.setItem("livato_automations",JSON.stringify(u)); return u; })}/>)}
              </div>
            </div>
          )}

          {tab==="more" && <MoreTab scenes={scenes} setScenes={setScenes} weather={weatherData} toTemp={toTempGlobal} onAddSuggestion={addSuggestion} existingIds={existingIds}/>}

          {tab==="automations" && building && (
            <div style={{ fontFamily:"sans-serif" }}>
              <div style={{ display:"flex",alignItems:"center",marginBottom:20 }}>
                {["Trigger","Condition","Actions","Save"].map((label,i)=>(
                  <div key={i} style={{ display:"flex",alignItems:"center",flex:i<3?1:"none" }}>
                    <div style={{ display:"flex",flexDirection:"column",alignItems:"center",gap:3 }}>
                      <div style={{ width:26,height:26,borderRadius:"50%",background:step>i+1?"#22c55e":step===i+1?"#5db832":"rgba(255,255,255,.07)",color:step>=i+1?"#fff":"#334155",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:800 }}>{step>i+1?"✓":i+1}</div>
                      <div style={{ fontSize:9,color:step===i+1?"#5db832":"#334155",fontWeight:600 }}>{label}</div>
                    </div>
                    {i<3 && <div style={{ flex:1,height:2,background:step>i+1?"#22c55e":"rgba(255,255,255,.07)",marginBottom:14 }}/>}
                  </div>
                ))}
              </div>
              {step===1 && (<div><div style={{ fontSize:17,fontWeight:700,marginBottom:3 }}>When this happens…</div><div style={{ color:"#475569",fontSize:13,marginBottom:13 }}>Choose the trigger device</div><DevicePicker value={bTD} onChange={id=>{ setBTD(id); setBTE(null); }} filterFn={d=>d.capabilities.triggers.length>0}/>{triggerCaps.length>0 && <div style={{ marginTop:12 }}><div style={{ color:"#64748b",fontSize:12,fontWeight:600,marginBottom:6 }}>What event?</div><OptionPicker value={bTE} onChange={setBTE} options={triggerCaps.map(c=>({value:c,label:TRIGGER_LABELS[c]||c}))}/></div>}<BtnPrimary disabled={!bTD||!bTE} onClick={()=>setStep(2)} style={{ marginTop:16,flex:"none",width:"100%" }}>Next: Condition →</BtnPrimary></div>)}
              {step===2 && (<div><div style={{ fontSize:17,fontWeight:700,marginBottom:3 }}>Add a condition</div><div style={{ color:"#475569",fontSize:13,marginBottom:13 }}>Limit when this runs (optional)</div><div style={{ background:"rgba(255,255,255,.04)",border:"1.5px solid rgba(255,255,255,.09)",borderRadius:15,padding:13,marginBottom:10 }}><div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:bUT?12:0 }}><div><div style={{ color:"#e2e8f0",fontWeight:600,fontSize:14 }}>🕐 Time of day</div><div style={{ color:"#475569",fontSize:11 }}>Only run between certain hours</div></div><Toggle on={bUT} onChange={setBUT}/></div>{bUT && <div style={{ display:"flex",gap:9 }}>{[["From",bTF,setBTF],["To",bTT,setBTT]].map(([l,v,s])=><div key={l} style={{ flex:1 }}><div style={{ color:"#64748b",fontSize:11,fontWeight:600,marginBottom:4 }}>{l}</div><input type="time" value={v} onChange={e=>s(e.target.value)} style={{ width:"100%",padding:"9px 11px",borderRadius:9,border:"1.5px solid rgba(255,255,255,.12)",background:"rgba(255,255,255,.06)",color:"#f1f5f9",fontSize:13,fontFamily:"inherit",outline:"none",colorScheme:"dark" }}/></div>)}</div>}</div><div style={{ background:"rgba(255,255,255,.04)",border:"1.5px solid rgba(255,255,255,.09)",borderRadius:15,padding:13 }}><div style={{ color:"#e2e8f0",fontWeight:600,fontSize:14,marginBottom:10 }}>📅 Days</div><div style={{ display:"flex",gap:4 }}>{DAYS.map((d,i)=>{ const a=bDays.includes(i); return <button key={d} onClick={()=>setBDays(p=>a?p.filter(x=>x!==i):[...p,i])} style={{ flex:1,padding:"6px 0",borderRadius:7,border:"1.5px solid",borderColor:a?"#5db832":"rgba(255,255,255,.08)",background:a?"rgba(93,184,50,.15)":"rgba(255,255,255,.03)",color:a?"#5db832":"#334155",fontSize:9,fontWeight:700,cursor:"pointer" }}>{d}</button>; })}</div></div><div style={{ display:"flex",gap:9,marginTop:14 }}><BackBtn onClick={()=>setStep(1)}/><BtnPrimary onClick={()=>setStep(3)}>Next: Actions →</BtnPrimary></div></div>)}
              {step===3 && (<div><div style={{ fontSize:17,fontWeight:700,marginBottom:3 }}>Then do this…</div><div style={{ color:"#475569",fontSize:13,marginBottom:13 }}>Add device controls and notifications</div><ActionBuilder actions={bActs} onChange={setBActs}/><div style={{ display:"flex",gap:9,marginTop:14 }}><BackBtn onClick={()=>setStep(2)}/><BtnPrimary disabled={!actsValid} onClick={()=>setStep(4)}>Next: Save →</BtnPrimary></div></div>)}
              {step===4 && (<div><div style={{ fontSize:17,fontWeight:700,marginBottom:3 }}>Review &amp; Save</div><div style={{ background:"rgba(255,255,255,.04)",border:"1.5px solid rgba(255,255,255,.09)",borderRadius:15,padding:15,marginBottom:12 }}>{[["Trigger",`${triggerDev?.icon} ${triggerDev?.name} — ${TRIGGER_LABELS[bTE]||bTE}`,"#6366f1"],bUT&&["Time",`${bTF} → ${bTT}`,"#06b6d4"],["Days",bDays.map(d=>DAYS[d]).join(", "),"#06b6d4"],...bActs.map((a,i)=>[`Action ${i+1}`,a.type==="notify"?`🔔 ${a.message}`:`${deviceById(a.deviceId)?.icon} ${ACTION_LABELS[a.action]} ${deviceById(a.deviceId)?.name}`,"#5db832"])].filter(Boolean).filter(r=>r[0]).map(([l,v,c])=>(<div key={l} style={{ display:"flex",gap:10,marginBottom:8 }}><span style={{ flexShrink:0,fontSize:10,fontWeight:700,textTransform:"uppercase",color:c+"99",marginTop:2,width:52 }}>{l}</span><span style={{ color:"#cbd5e1",fontSize:13 }}>{v}</span></div>))}</div><input value={bName} onChange={e=>setBName(e.target.value)} placeholder="Name your automation…" style={{ width:"100%",padding:"12px 14px",borderRadius:12,border:"1.5px solid rgba(255,255,255,.12)",background:"rgba(255,255,255,.05)",color:"#f1f5f9",fontSize:14,fontFamily:"inherit",outline:"none",boxSizing:"border-box",marginBottom:12 }}/>{saved?<div style={{ padding:14,borderRadius:14,background:"linear-gradient(135deg,#22c55e,#16a34a)",color:"#fff",fontWeight:700,fontSize:15,textAlign:"center" }}>✓ Automation saved and live!</div>:<div style={{ display:"flex",gap:9 }}><BackBtn onClick={()=>setStep(3)}/><BtnPrimary onClick={saveAuto}>✦ Save Automation</BtnPrimary></div>}<button onClick={resetBuilder} style={{ width:"100%",marginTop:8,padding:8,background:"none",border:"none",color:"#334155",fontSize:12,cursor:"pointer" }}>Cancel</button></div>)}
            </div>
          )}
        </div>
      </div>

      {/* Persistent YouTube Player */}
      {ytTrackId && <PersistentPlayer trackId={ytTrackId} />}

      {/* Bottom nav */}
      <div style={{ position:"fixed",bottom:0,left:0,right:0,zIndex:100,display:"flex",justifyContent:"center",background:"linear-gradient(to top,rgba(8,15,5,1) 55%,transparent)" }}>
        <div style={{ display:"flex",gap:1,background:"rgba(10,18,6,.95)",backdropFilter:"blur(20px)",border:"1.5px solid rgba(255,255,255,.06)",borderRadius:22,padding:5,margin:"0 16px 16px" }}>
          {TABS.map(t => {
            const active = tab===t.key && !building;
            return (
              <button key={t.key} onClick={()=>{ setTab(t.key); resetBuilder(); }} style={{ display:"flex",flexDirection:"column",alignItems:"center",gap:2,padding:"8px 13px",borderRadius:17,border:"none",background:active?"rgba(93,184,50,.13)":"transparent",cursor:"pointer" }}>
                <span style={{ fontSize:16 }}>{t.icon}</span>
                <span style={{ fontSize:9,fontWeight:700,color:active?"#5db832":"#334155",fontFamily:"sans-serif" }}>{t.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <style>{`
        :root {
          --primary: ${(() => { const t=THEMES.find(x=>x.id===(localStorage.getItem("livato_theme")||"green")); return t?.primary||"#5db832"; })()};
        }
        @keyframes fadeUp{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
        @keyframes blink{0%,100%{opacity:.4}50%{opacity:.15}}
        @keyframes dot{0%,80%,100%{transform:scale(.6);opacity:.4}40%{transform:scale(1);opacity:1}}
        @keyframes bar0{from{height:4px}to{height:22px}}@keyframes bar1{from{height:4px}to{height:16px}}@keyframes bar2{from{height:4px}to{height:28px}}@keyframes bar3{from{height:4px}to{height:19px}}@keyframes bar4{from{height:4px}to{height:12px}}
        button:active{transform:scale(.97)}
        input[type="range"]{appearance:none;height:4px;border-radius:3px;background:rgba(255,255,255,.08);outline:none;cursor:pointer}
        input[type="range"]::-webkit-slider-thumb{appearance:none;width:17px;height:17px;border-radius:50%;background:#5db832;box-shadow:0 0 8px rgba(93,184,50,.5);cursor:pointer}
        *{box-sizing:border-box}::-webkit-scrollbar{width:0;height:0}
        input::placeholder{color:#1e293b}
      `}</style>
    </div>
  );
}